import React, { useEffect, useState, useMemo } from "react";
import style from "../../../../styles/brsr/general.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  TextField,
  Typography,
} from "@mui/material";
import DynamicTable from "../../../Common/CommonDynamicTable";
import styles from "../../../../styles/common/brsrTable.module.css";
import { Add, Delete } from "@mui/icons-material";
import EastIcon from "@mui/icons-material/East";
import { get, put } from "../../../../utils/api";
import { useParams } from "react-router-dom";
import useReportingYearLabels from "../../../BRSRReport/SectionCPrinciples/useReportingYearLabels";
import { brsrReview, reviewBRSR } from "../../../../utils/companyId";
import ReviewBox from "../../../Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";
import DeleteRowDialog from "../../../Common/DeleteRowDialog";
import BRSRFile from "../../../Common/BRSRFile";

const normWasteIntensityText = (v) =>
  v == null || v === "" ? "" : String(v);

/** BRSR waste intensity (9b): four rows × current/previous year — safe for fetch and save. */
const mapWasteIntensityFields = (wi) => {
  const src = wi && typeof wi === "object" ? wi : {};
  const row = (key) => ({
    currentFinancialYear: normWasteIntensityText(src?.[key]?.currentFinancialYear),
    previousFinancialYear: normWasteIntensityText(
      src?.[key]?.previousFinancialYear,
    ),
  });
  return {
    wasteIntensity: row("wasteIntensity"),
    wasteIntensityPPP: row("wasteIntensityPPP"),
    wasteIntensityPhysical: row("wasteIntensityPhysical"),
    wasteIntensityOptiomal: row("wasteIntensityOptiomal"),
  };
};

/** 9b mandatory rows — numeric cells only; optional row key is `wasteIntensityOptiomal`. */
const WASTE_INTENSITY_NUMERIC_ROW_KEYS = [
  "wasteIntensity",
  "wasteIntensityPPP",
  "wasteIntensityPhysical",
];

const sanitizeWasteIntensityNumericInput = (raw) => {
  const s = String(raw ?? "");
  let out = "";
  let hasDot = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c >= "0" && c <= "9") out += c;
    else if (c === "." && !hasDot) {
      hasDot = true;
      out += c;
    }
  }
  return out;
};

const isWasteIntensityMandatoryNumber = (v) => {
  const t = String(v ?? "").trim();
  if (t === "") return false;
  return /^[0-9]+(\.[0-9]*)?$/.test(t);
};

const isApplicableYes = (value) => value === "Yes" || value === true;

const toApplicableLabel = (value) =>
  value === false || value === "No" ? "No" : "Yes";

const emptyProjectUndertakenRow = {
  nameOfProject: "",
  projectBrief: "",
  EIANotification: "",
  dateOfNotification: "",
  publicDomain: "",
  extenalAgecy: "",
  link: "",
};

const ReusableTable = ({
  data,
  formData,
  handleInputChange,
  fieldPrefix,
  labels,
  isEnergyIntensity,
  energySource,
  energySourceNonRenewable,
  reusableErrors,
  disabled,
  wrapValueCells = false,
  digitOnlyKeys = null,
}) => {
  const calculateTotal = (field) => {
    const total = data
      .filter(({ key }) => key !== "total")
      .reduce((sum, { key }) => {
        const value = parseFloat(formData[fieldPrefix][key]?.[field]) || 0;
        return sum + value;
      }, 0);
    return Number.isInteger(total) ? total : total.toFixed(3);
  };

  const calculateTotalForTable = (tablePrefix, field) => {
    const tableData =
      tablePrefix === "fromRenewableSource"
        ? energySource
        : energySourceNonRenewable;
    const total = tableData
      .filter(({ key }) => key !== "total")
      .reduce((sum, { key }) => {
        const value = parseFloat(formData[tablePrefix][key]?.[field]) || 0;
        return sum + value;
      }, 0);
    return Number.isInteger(total) ? total : total.toFixed(3);
  };

  const calculateEnergyIntensity = (field) => {
    const renewableTotal = Number(
      calculateTotalForTable("fromRenewableSource", field),
    );
    const nonRenewableTotal = Number(
      calculateTotalForTable("fromNonRenewableSource", field),
    );
    const total = renewableTotal + nonRenewableTotal;
    return Number.isInteger(total) ? total : Number(total.toFixed(3));
  };

  const hasSerialNumberColumn = labels.headings.some(
    (heading) => heading.label === "S. No",
  );

  const getTableErrors = () => {
    const errors = [];

    data.forEach(({ label, key }) => {
      // Skip error checking for optional waste intensity field
      if (
        fieldPrefix === "wasteIntensity" &&
        key === "wasteIntensityOptiomal"
      ) {
        return;
      }

      ["currentFinancialYear", "previousFinancialYear"].forEach((field) => {
        if (reusableErrors[fieldPrefix]?.[key]?.[field]) {
          const fieldLabel = field.replace(/([A-Z])/g, " $1");
          const suffix =
            fieldPrefix === "wasteIntensity" && key !== "wasteIntensityOptiomal"
              ? " (numbers only)"
              : " is required";
          errors.push(`• ${label} - ${fieldLabel}${suffix}`);
        }
      });
    });

    return errors;
  };

  const tableErrors = getTableErrors();

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          marginTop: "-0.7rem",
          mb: "1rem",
          borderRadius: "10px",
          ...(wrapValueCells && { overflow: "visible" }),
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1E4976", color: "#fff" }}>
              {labels.headings
                .filter((heading) => heading.label !== "Unit")
                .map((heading, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      textAlign: "left",
                      border: "1px solid #ddd",
                      width: heading?.width,
                      fontSize: "14px",
                      fontFamily: "inter",
                      ...(wrapValueCells && {
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        verticalAlign: "top",
                      }),
                    }}
                    colSpan={heading.colSpan}
                  >
                    {heading.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((el, index) => (
              <TableRow key={index}>
                {hasSerialNumberColumn && (
                  <TableCell
                    sx={{
                      border: "1px solid #ddd",
                      padding: "4px 8px",
                      textAlign: "center",
                    }}
                  >
                    {index + 1}.
                  </TableCell>
                )}
                <TableCell
                  sx={
                    wrapValueCells
                      ? {
                          maxWidth: "44%",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                          verticalAlign: "top",
                        }
                      : undefined
                  }
                >
                  {el?.label.includes("Other Hazardous waste.") ? (
                    <TextField
                      fullWidth
                      size="small"
                      disabled={disabled}
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        fontFamily: "inter",
                      }}
                      multiline
                      placeholder={el?.placeholder}
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        className: "textField",
                      }}
                      value={
                        formData[fieldPrefix][el?.key]?.["otherHazardous"] || ""
                      }
                      onChange={(e) =>
                        handleInputChange(
                          fieldPrefix,
                          el?.key,
                          "otherHazardous",
                          e.target.value,
                        )
                      }
                    />
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        fontFamily: "inter",
                        ...style,
                      }}
                      placeholder={el?.placeholder}
                    >
                      {el?.label.includes("Other Hazardous waste")
                        ? ""
                        : el?.label || ""}
                    </Typography>
                  )}
                </TableCell>

                {["currentFinancialYear", "previousFinancialYear"].map(
                  (field, subIndex) => (
                    <TableCell
                      key={subIndex}
                      sx={{
                        border: "1px solid #ddd",
                        // padding: "4px 8px",
                        backgroundColor:
                          el?.key === "total" ? "#EFEFEF" : "transparent",
                        ...(wrapValueCells && {
                          overflow: "visible",
                          verticalAlign: "top",
                          minWidth: 0,
                        }),
                      }}
                    >
                      {isEnergyIntensity === true && index === 0 ? (
                        <>
                          {" "}
                          <TextField
                            fullWidth
                            size="small"
                            variant="standard"
                            disabled={disabled || true}
                            InputProps={{
                              disableUnderline: true,
                              className: "textField",
                            }}
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              fontFamily: "inter",
                            }}
                            multiline
                            placeholder="Total"
                            value={calculateEnergyIntensity(field) ?? ""}
                          />
                        </>
                      ) : el?.key === "total" ? (
                        <TextField
                          fullWidth
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "14px",
                            fontFamily: "inter",
                          }}
                          disabled={disabled || true}
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            className: "textField",
                          }}
                          multiline
                          placeholder="Total"
                          value={calculateTotal(field) ?? ""}
                        />
                      ) : (
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            disabled={disabled}
                            inputProps={{
                              inputMode:
                                wrapValueCells &&
                                Array.isArray(digitOnlyKeys) &&
                                digitOnlyKeys.includes(el.key)
                                  ? "decimal"
                                  : undefined,
                            }}
                            onKeyPress={(e) => {
                              const restrictDigits = wrapValueCells
                                ? Array.isArray(digitOnlyKeys) &&
                                  digitOnlyKeys.includes(el.key)
                                : true;
                              if (!restrictDigits) return;
                              if (!/[0-9.]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            sx={{
                              fontWeight: 500,
                              fontSize: "14px",
                              fontFamily: "inter",
                              ...(wrapValueCells && {
                                "& .MuiInputBase-root": {
                                  overflow: "visible",
                                  alignItems: "flex-start",
                                },
                                "& .MuiInputBase-input": {
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-all",
                                  overflow: "visible",
                                },
                              }),
                            }}
                            multiline
                            minRows={wrapValueCells ? 1 : undefined}
                            placeholder={labels.placeholder}
                            variant="standard"
                            InputProps={{
                              disableUnderline: true,
                              className: "textField",
                            }}
                            value={
                              formData[fieldPrefix][el?.key]?.[field] ?? ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                fieldPrefix,
                                el?.key,
                                field,
                                e.target.value,
                              )
                            }
                            // error={!!reusableErrors[fieldPrefix]?.[key]?.[field]}
                            // sx={{
                            //   "& .MuiInput-input": {
                            //     border: reusableErrors[fieldPrefix]?.[key]?.[
                            //       field
                            //     ]
                            //       ? "1px solid red"
                            //       : "none",
                            //   },
                            // }}
                          />
                          {/* {reusableErrors[fieldPrefix]?.[key]?.[field] && (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.75rem",
                              marginTop: "2px",
                            }}
                          >
                            {`${label} ${field.replace(
                              /([A-Z])/g,
                              " $1"
                            )} is required`}
                          </span>
                        )} */}
                        </div>
                      )}
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {tableErrors.length > 0 && (
        <div
          style={{
            color: "red",
            marginTop: "8px",
            fontSize: "0.875rem",
            paddingLeft: "16px",
          }}
        >
          {tableErrors.map((error, index) => (
            <Typography
              key={index}
              variant="body2"
              color="error"
              style={{
                lineHeight: "1.2",
                margin: "0 0 0 -0.8rem",
                color: "#d34040",
                fontSize: "10px",
                fontStyle: "italic",
              }}
            >
              {error}
            </Typography>
          ))}
        </div>
      )}
    </>
  );
};

const WaterReusableTable = ({ title, rows, columns, onChange, disabled }) => {
  const calculateTotal = (row) => {
    const { surfaceWater, groundWater, thirdPartyWater, seaWater, other } = row;
    return (
      parseFloat(surfaceWater.currentFinancialYear || 0) +
      parseFloat(groundWater.currentFinancialYear || 0) +
      parseFloat(thirdPartyWater.currentFinancialYear || 0) +
      parseFloat(seaWater.currentFinancialYear || 0) +
      parseFloat(other.currentFinancialYear || 0)
    );
  };

  return (
    <div>
      {title && <p className={styles.tableTitle}>{title}</p>}
      <TableContainer
        component={Paper}
        className={styles.tableContainer}
        sx={{ borderRadius: "10px", marginTop: "-0.7rem" }}
      >
        <Table className={styles.table}>
          <TableHead>
            <TableRow className={styles.tableHeader}>
              {columns.map((col, colIndex) => (
                <TableCell
                  key={colIndex}
                  className={styles.tableHeaderCell}
                  sx={{
                    width: col?.width,
                    fontFamily: "inter",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
            <TableRow className={styles.tableHeader}>
              <TableCell
                colSpan={3}
                align="left"
                className={styles.tableHeaderCell}
                sx={{ padding: "7px 15px" }}
              >
                Water withdrawal by source (in kiloliters)
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((col, colIndex) => {
                  const isTotalField =
                    row.parameter ===
                      "Total volume of water withdrawal (i + ii + iii + iv + v)" &&
                    (col.field === "currentFinancialYear" ||
                      col.field === "previousFinancialYear");

                  return (
                    <TableCell
                      key={colIndex}
                      className={styles.tableCell}
                      sx={{
                        backgroundColor: isTotalField ? "#EFEFEF" : "inherit",
                        color: isTotalField ? "#222222" : "inherit",
                        fontWeight: isTotalField ? "bold" : "normal",
                        width: col?.width,
                      }}
                    >
                      {col.type === "text" ? (
                        <TextField
                          size="small"
                          fullWidth
                          disabled={disabled || isTotalField}
                          value={row[col.field] ?? ""}
                          onChange={(e) =>
                            onChange(rowIndex, col.field, e.target.value)
                          }
                          multiline
                          placeholder={isTotalField ? "Total" : "Enter Value"}
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            className: styles.textField,
                            sx: {
                              backgroundColor: isTotalField
                                ? "#EFEFEF"
                                : "inherit",
                              fontSize: "14px",
                              color: isTotalField ? "#222222" : "inherit",
                              fontWeight:
                                rowIndex > 4 || isTotalField ? 600 : 500,
                              fontFamily: "inter",
                            },
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9.]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className={styles.textFieldContainer}
                        />
                      ) : (
                        row[col.field]
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

const ReusableRadioGroup = ({
  value,
  name,
  onChange,
  labels = { yes: "Yes", no: "No" },
  disabled,
}) => {
  return (
    <FormControl component="fieldset">
      <RadioGroup
        name={name}
        value={value ? "yes" : "no"}
        onChange={onChange}
        disabled={disabled}
        style={{
          display: "flex",
          flexDirection: "row",
          marginBottom: "10px",
          marginTop: "-10px",
          margin: "-10px 0px 10px 10px",
        }}
      >
        <FormControlLabel
          value="yes"
          control={<Radio disabled={disabled} />}
          label={labels.yes}
        />
        <FormControlLabel
          value="no"
          control={<Radio disabled={disabled} />}
          label={labels.no}
        />
      </RadioGroup>
    </FormControl>
  );
};

const EssentialIndicator = ({
  onSave,
  hide,
  removeUpload = false,
  downloadMode = false,
}) => {
  const { id } = useParams();
  const { currentYear: reportingFY, previousYear: reportingPreviousFY } =
    useReportingYearLabels();

  const wasteIntensityColumnLabels = useMemo(
    () => ({
      headings: [
        { label: "Parameters", width: "44%" },
        {
          label: reportingFY
            ? `FY ${reportingFY} (Current Financial Year)`
            : "Current Financial Year",
          width: "28%",
        },
        {
          label: reportingPreviousFY
            ? `FY ${reportingPreviousFY} (Previous Financial Year)`
            : "Previous Financial Year",
          width: "28%",
        },
      ],
      placeholder: "Enter Number",
    }),
    [reportingFY, reportingPreviousFY],
  );

  const [disabled, setDisabled] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [modalupload, setModalUpload] = useState({
    energyConsumptionIntensityDoc: false,
    patSchemeDoc: false,
    waterDisclosuresRelatedDoc: false,
    waterDischargeDoc: false,
    zeroLiquidDischargeDoc: false,
    airEmissionDoc: false,
    greenHouseGasEmissionsDoc: false,
    greenHouseGasBooleanEmissionsDoc: false,
    wasteManagementDoc: false,
    wasterManagmentPracticesDoc: false,
    environmentalApprovalDoc: false,
    projectUndertakenByentityDoc: false,
    entityawDoc: false,
  });
  const initialWasteData = {
    currentFinancialYear: "",
    currentUnit: "",
    previousFinancialYear: "",
    previousUnit: "",
  };

  const greenHouseGas = {
    currentFinancialYear: "",
    previousFinancialYear: "",
  };

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedUnit1, setSelectedUnit1] = useState("");
  const [unitErrors, setUnitErrors] = useState({
    selectedUnit: false,
    selectedUnit1: false,
  });

  const [rows, setRows] = useState("");
  const [errors, setErrors] = useState([]);

  const initTwoYearField = () => ({
    currentFinancialYear: false,
    previousFinancialYear: false,
  });
  const [reusableErrors, setReusableErrors] = useState({
    fromRenewableSource: {
      electricityConsumption: initTwoYearField(),
      fuelConsumption: initTwoYearField(),
      energyConsumption: initTwoYearField(),
      total: initTwoYearField(),
    },
    fromNonRenewableSource: {
      electricityConsumption: initTwoYearField(),
      fuelConsumption: initTwoYearField(),
      energyConsumption: initTwoYearField(),
      total: initTwoYearField(),
    },
    energyIntensity: {
      energyIntensity: initTwoYearField(),
      energyIntensityTurnover: initTwoYearField(),
      energyIntensityPuchasingPower: initTwoYearField(),
      energyPhysicalOutput: initTwoYearField(),
      energyIntensityOptional: initTwoYearField(),
    },
    wasteGenerated: {
      plastics: initTwoYearField(),
      eWaste: initTwoYearField(),
      bioMedical: initTwoYearField(),
      constructionAndDemolition: initTwoYearField(),
      batteryWaste: initTwoYearField(),
      radioactiveWaste: initTwoYearField(),
      otherHazardous: initTwoYearField(),
      otherNonHazardous: initTwoYearField(),
      total: initTwoYearField(),
    },
    wasteIntensity: {
      wasteIntensity: initTwoYearField(),
      wasteIntensityPPP: initTwoYearField(),
      wasteIntensityPhysical: initTwoYearField(),
      wasteIntensityOptiomal: initTwoYearField(),
    },

    wasteGeneratedCategory: {
      recycled: initTwoYearField(),
      reUsed: initTwoYearField(),
      other: initTwoYearField(),
      total: initTwoYearField(),
    },

    wasteDesposed: {
      inceneration: initTwoYearField(),
      lanfilling: initTwoYearField(),
      other: initTwoYearField(),
      total: initTwoYearField(),
    },

    airEmissons: {
      nox: initTwoYearField(),
      voc: initTwoYearField(),
      sox: initTwoYearField(),
      pm: initTwoYearField(),
      pop: initTwoYearField(),
      hap: initTwoYearField(),
    },

    greenHouseGasEmissions: {
      scope1: initTwoYearField(),
      scope2: initTwoYearField(),
      totalOfScope1and2Intensity: initTwoYearField(),
      scope1and2emissionPPP: initTwoYearField(),
      scope1and2emissionIntensityPhysical: initTwoYearField(),
      scope1and2EmissionIntensity: initTwoYearField(),
      // externalAgency: {
      //   action: true,
      //   name: [""],
      // },
    },
  });
  const [waterDischargeErrors, setWaterDischargeErrors] = useState({
    current: {
      surfaceWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      groundWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      producedWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      thirdPartyWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      other: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
    },
    previous: {
      surfaceWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      groundWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      producedWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      thirdPartyWater: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
      other: {
        dischargeWithoutTreatment: false,
        dischargeAfterTreatment: false,
        treatmentLevel: false,
      },
    },
  });
  // const [errorMessage, setErrorMessage] = useState([]);
  const [errorMessages, setErrorMessages] = useState({
    energyIndependentAssessment: [],
    airEmissionAssessment: [],
    waterDischargeExternalAgency: [],
    greenHouseGasEmissions: [],
    waterDisclosuresRelated: [],
    independentAssessment: [],
  });
  const [validationErrors, setValidationErrors] = useState({
    patSchemeDetails: false,
    zeroLiquidDischarge: false,
    greenHouseGasBooleanEmissions: false,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fieldToRemove, setFieldToRemove] = React.useState({
    tableField: null,
    rowIndex: null,
  });
  const [openDialog, setOpenDialog] = React.useState(false);
  const [formData, setFormData] = useState({
    // energyConsumptionIntensityDoc: false,
    // patSchemeDoc: false,
    // waterDisclosuresRelatedDoc: false,
    // waterDischargeDoc: false,
    // zeroLiquidDischargeDoc: false,
    // airEmissionDoc: false,
    // greenHouseGasEmissionsDoc: false,
    // greenHouseGasBooleanEmissionsDoc: false,
    // wasteManagementDoc: false,
    // wasterManagmentPracticesDoc: false,
    // environmentalApprovalDoc: false,
    // projectUndertakenByentityDoc: false,
    // entityawDoc: false,
    // 1
    fromRenewableSource: {
      electricityConsumption: { ...initialWasteData },
      fuelConsumption: { ...initialWasteData },
      energyConsumption: { ...initialWasteData },
      // total: "",
      // total: { currentFinancialYear: 0, previousFinancialYear: 0 }, // ✅ Initialize as an object
    },

    fromNonRenewableSource: {
      electricityConsumption: { ...initialWasteData },
      fuelConsumption: { ...initialWasteData },
      energyConsumption: { ...initialWasteData },
      // total: "",
      // total: { currentFinancialYear: 0, previousFinancialYear: 0 }, // ✅ Initialize as an object
    },

    energyIntensity: {
      energyIntensity: { currentFinancialYear: 0, previousFinancialYear: 0 },
      // energyIntensity: { ...initialWasteData },
      energyIntensityTurnover: { ...initialWasteData },
      energyIntensityPuchasingPower: { ...initialWasteData },
      energyPhysicalOutput: { ...initialWasteData },
      energyIntensityOptional: { ...initialWasteData },
    },

    energyIndependentAssessment: {
      action: true,
      name: [""],
    },

    patSchemeDetails: {
      action: true,
      remark: "",
    },

    environmentalApproval: [
      {
        serialNumber: 1,
        location: "",
        operation: "",
        approval: "",
        reason: "",
      },
    ],
    entityComplain: true,

    entityComplainwithEnvironmentLaw: [
      {
        serialNumber: 1,
        specifyLaw: "",
        details: "",
        penalties: "",
        correctiveAction: "",
      },
    ],
    projectUndertakenByentity: [
      {
        serialNumber: 1,
        ...emptyProjectUndertakenRow,
      },
    ],
    eiaEnabled: "Yes",
    wasteGenerated: {
      plastics: { ...greenHouseGas },
      eWaste: { ...greenHouseGas },
      bioMediacal: { ...greenHouseGas },
      construtionAndDemolition: { ...greenHouseGas },
      batteryWaste: { ...greenHouseGas },
      radioactiveWaste: { ...greenHouseGas },
      otherHazardous: { otherHazardous: "", ...greenHouseGas },
      otherNonHazardous: { ...greenHouseGas },
      total: "",
    },
    wasteIntensity: {
      wasteIntensity: { ...greenHouseGas },
      wasteIntensityPPP: { ...greenHouseGas },
      wasteIntensityPhysical: { ...greenHouseGas },
      wasteIntensityOptiomal: { ...greenHouseGas },
    },

    wasteGeneratedCategory: {
      recycled: { ...greenHouseGas },
      reUsed: { ...greenHouseGas },
      other: { ...greenHouseGas },
      total: "",
    },

    wasteDesposed: {
      inceneration: { ...greenHouseGas },
      lanfilling: { ...greenHouseGas },
      other: { ...greenHouseGas },
      total: "",
    },

    independentAssessment: {
      action: true,
      name: [""],
    },
    // 7
    greenHouseGasEmissions: {
      scope1: { ...greenHouseGas },
      scope2: { ...greenHouseGas },
      totalOfScope1and2Intensity: { ...greenHouseGas },
      scope1and2emissionPPP: { ...greenHouseGas },
      scope1and2emissionIntensityPhysical: { ...greenHouseGas },
      scope1and2EmissionIntensity: { ...greenHouseGas },
      externalAgency: {
        action: true,
        name: [""],
      },
    },

    airEmissons: {
      nox: { ...initialWasteData },
      voc: { ...initialWasteData },
      sox: { ...initialWasteData },
      pm: { ...initialWasteData },
      pop: { ...initialWasteData },
      hap: { ...initialWasteData },
    },
    airEmissionsOthers: [
      {
        parameter: "",
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
    ],
    airEmissionAssessment: {
      action: true,
      name: [""],
    },
    // 8
    greenHouseGasBooleanEmissions: {
      action: true,
      remark: "",
    },

    // 10
    wasterManagmentPractices: "",

    // 5
    zeroLiquidDischarge: {
      action: true,
      remark: "",
    },

    // 3
    waterDisclosuresRelated: {
      surfaceWater: { currentFinancialYear: "", previousFinancialYear: "" },
      groundWater: { currentFinancialYear: "", previousFinancialYear: "" },
      thirdPartyWater: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      seaWater: { currentFinancialYear: "", previousFinancialYear: "" },
      others: { currentFinancialYear: "", previousFinancialYear: "" },
      totalVolumnWithdrawal: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      totalWaterConsumption: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      waterIntensityTurnover: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      waterIntensityPuchasingPower: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      waterIntensityPhyiscalOutput: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      waterIntensityOptional: {
        currentFinancialYear: "",
        previousFinancialYear: "",
      },
      externalAgency: {
        action: true,
        name: [""],
      },
    },

    // 4
    waterDischargeCurrent: {
      surfaceWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      groundWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      producedWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      thirdPartyWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      other: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      total: "",
    },
    waterDischargePrevious: {
      surfaceWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      groundWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      producedWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      thirdPartyWater: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      other: {
        dischargeWithoutTreatment: "",
        treatmentLevel: "",
        dischargeAfterTreatment: "",
        unit: "KL",
      },
      total: "",
    },
    waterDischargeExternalAgency: {
      action: true,
      name: [""],
    },
  });
  console.log("errors", errors);

  const handleTableChange = (tableField, rowIndex, key, value) => {
    setFormData((prevState) => {
      const updatedTable = [...prevState[tableField]];
      const previousRow = updatedTable[rowIndex] || {};
      const nextRow = { ...previousRow, [key]: value };

      // If approval is true, ensure reason is cleared
      if (
        (key === "approval" && value === true) ||
        nextRow?.approval === true
      ) {
        nextRow.reason = "";
      }

      updatedTable[rowIndex] = nextRow;
      return { ...prevState, [tableField]: updatedTable };
    });
  };

  const addRowToTable = (tableField, defaultRow) => {
    setFormData((prevState) => ({
      ...prevState,
      [tableField]: [...prevState[tableField], defaultRow],
    }));
  };

  const confirmRemoveRow = () => {
    if (fieldToRemove.tableField && fieldToRemove.rowIndex !== null) {
      setFormData((prev) => {
        const updatedTable = [...prev[fieldToRemove.tableField]];
        updatedTable.splice(fieldToRemove.rowIndex, 1);
        return {
          ...prev,
          [fieldToRemove.tableField]: updatedTable.map((row, index) => ({
            ...row,
            serialNumber: index + 1,
          })),
        };
      });
      setOpenDialog(false);
      setFieldToRemove({ tableField: null, rowIndex: null });
    }
  };

  const cancelRemoveRow = () => {
    setOpenDialog(false);
    setFieldToRemove({ tableField: null, rowIndex: null });
  };

  const checkRowHasData = (row, fieldsToCheck) => {
    return fieldsToCheck.some((field) => row[field]?.trim() !== "");
  };

  // Modified delete handler for tables
  const handleDeleteRow = (tableField, rowIndex, fieldsToCheck) => {
    const currentRow = formData[tableField][rowIndex];

    if (checkRowHasData(currentRow, fieldsToCheck)) {
      // Show confirmation dialog if data exists
      setFieldToRemove({ tableField, rowIndex });
      setOpenDialog(true);
    } else {
      // Immediately delete empty row
      setFormData((prev) => {
        const updatedTable = [...prev[tableField]];
        updatedTable.splice(rowIndex, 1);
        return {
          ...prev,
          [tableField]: updatedTable.map((row, index) => ({
            ...row,
            serialNumber: index + 1,
          })),
        };
      });
    }
  };

  const environmentColumns = [
    { label: "S.No", field: "serialNumber", type: "static" },
    {
      label: "Specify the law / regulation / guidelines (not complied)",
      field: "specifyLaw",
      type: "text",
    },
    {
      label: "Provide details of the non- compliance",
      field: "details",
      type: "text",
    },
    {
      label:
        "Any fines / penalties / action taken by regulatory agencies such as pollution control boards or by courts",
      field: "penalties",
      type: "text",
    },
    {
      label: "Corrective action taken, if any",
      field: "correctiveAction",
      type: "text",
    },
  ];

  const environmentAssesmentColumns = [
    { label: "S.No", field: "serialNumber", type: "static", width: "5%" },
    {
      label: "Name of Project",
      field: "nameOfProject",
      type: "text",
      width: "12%",
    },
    {
      label: "Brief Details of Project",
      field: "projectBrief",
      width: "12%",
      type: "text",
    },
    {
      label: "EIA Notification No.",
      field: "EIANotification",
      width: "12%",
      type: "text",
    },
    {
      label: "Date of Notification",
      field: "dateOfNotification",
      type: "date",
      width: "15%",
    },
    {
      label: "Whether conducted by independent external agency",
      field: "extenalAgecy",
      type: "select",
      width: "16%",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    {
      label: "Results communicated in public domain",
      field: "publicDomain",
      type: "select",
      width: "16%",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    {
      label: "Link",
      field: "link",
      type: "text",
      width: "10%",
    },
  ];

  const operationsColumns = [
    { label: "S.No", field: "serialNumber", type: "static" },
    {
      label: "Location of Operation",
      field: "location",
      type: "text",
    },
    {
      label: "Type of Operation",
      field: "operation",
      type: "text",
    },
    {
      label:
        "Whether the conditions of environmental approval / clearance are being complied with? ",
      field: "approval",
      type: "select",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    {
      label: "If no, the reasons thereof and corrective action taken, if any.",
      field: "reason",
      type: "text",
    },
  ];

  const wasteCategory = [
    { label: "Incineration", key: "inceneration" },
    { label: "Landfilling", key: "lanfilling" },
    { label: "Other disposal operations", key: "other" },
    {
      label: "Total",
      key: "total",
      style: { fontWeight: "bold" },
    },
  ];

  const wasteRecycledCategory = [
    { label: "Recycled", key: "recycled" },
    { label: "Re-Used", key: "reUsed" },
    { label: "Other recovery operations", key: "other" },
    {
      label: "Total",
      key: "total",
      style: { fontWeight: "bold" },
    },
  ];

  const wasteGenerated = [
    { label: "Plastic Waste", key: "plastics" },
    { label: "E-Waste", key: "eWaste" },
    { label: "Bio-Medical Waste", key: "bioMediacal" },
    {
      label: "Construction & Demolition Waste",
      key: "construtionAndDemolition",
    },
    { label: "Battery Waste", key: "batteryWaste" },
    { label: "Radioactive Waste", key: "radioactiveWaste" },
    {
      label: "Other Hazardous waste. Please specify, if any.",
      key: "otherHazardous",
      placeholder: "Other Hazardous waste. Please specify, if any.",
    },
    {
      label:
        "Other Non-hazardous waste generated (H). Please specify, if any.(Break-up by composition i.e. by materials relevant to the sector)",
      key: "otherNonHazardous",
      placeholder:
        "Other Non-hazardous waste generated (H). Please specify, if any.(Break-up by composition i.e. by materials relevant to the sector)",
    },
    { label: "Total", key: "total", style: { fontWeight: "bold" } },
  ];

  const wasteIntensity = [
    {
      label:
        "Waste intensity per rupee of turnover (Total waste generated / Revenue from operations)",
      key: "wasteIntensity",
    },
    {
      label:
        "Waste intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total waste generated / Revenue from operations adjusted for PPP)",
      key: "wasteIntensityPPP",
    },
    {
      label: "Waste intensity in terms of physical output",
      key: "wasteIntensityPhysical",
    },
    {
      label:
        "Waste intensity (optional) – the relevant metric may be selected by the entity",
      key: "wasteIntensityOptiomal",
    },
  ];

  const airEmissions = [
    { label: "NOx", key: "nox", style: { color: "#1C486B" } },
    {
      label: "Volatile organic compounds (VOC)",
      key: "voc",
      style: { color: "#1C486B" },
    },
    { label: "SOx", key: "sox", style: { color: "#1C486B" } },
    {
      label: "Particulate Matter (PM)",
      key: "pm",
      style: { color: "#1C486B" },
    },
    {
      label: "Persistent organic pollutants (POP)",
      key: "pop",
      style: { color: "#1C486B" },
    },
    {
      label: "Hazardous air pollutants (HAP)",
      key: "hap",
      style: { color: "#1C486B" },
    },
  ];

  const greenGasEmissions = [
    {
      label:
        "Total Scope 1 emissions Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
      key: "scope1",
      style: { color: "#1C486B" },
    },
    {
      label:
        "Total Scope 2 emissions Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
      key: "scope2",
      style: { color: "#1C486B" },
    },
    {
      label:
        "Total Scope 1 and Scope 2 emission intensity per rupee of turnover",
      key: "totalOfScope1and2Intensity",
      style: { color: "#1C486B" },
    },
    {
      label:
        "Total Scope 1 and Scope 2 emissions per rupee of turnover adjusted for Purchasing Power Parity (PPP)",
      key: "scope1and2emissionPPP",
      style: { color: "#1C486B" },
    },
    {
      label:
        "Total Scope 1 and Scope 2 emission intensity in terms of physical output",
      key: "scope1and2emissionIntensityPhysical",
      style: { color: "#1C486B" },
    },
    {
      label: "Total Scope 1 and Scope 2 emission intensity (optional)",
      key: "scope1and2EmissionIntensity",
      style: { color: "#1C486B" },
    },
  ];

  const energySource = [
    { label: "Total electricity consumption", key: "electricityConsumption" },
    { label: "Total fuel consumption", key: "fuelConsumption" },
    {
      label: "Energy consumption through other sources",
      key: "energyConsumption",
    },
    { label: "Total energy consumption from renewable sources", key: "total" },
  ];

  const energySourceNonRenewable = [
    { label: "Total electricity consumption", key: "electricityConsumption" },
    { label: "Total fuel consumption", key: "fuelConsumption" },
    {
      label: "Energy consumption through other sources",
      key: "energyConsumption",
    },
    {
      label: "Total energy consumption from Non renewable sources",
      key: "total",
    },
  ];

  const energyIntensity = [
    {
      label: "Total energy consumption: from renewable & non-renewable sources",
      key: "total",
    },
    {
      label: "Energy intensity per rupee of turnover",
      key: "energyIntensityTurnover",
    },
    {
      label:
        "Energy intensity per rupee of turnover adjusted for Purchasing Power Parity",
      key: "energyIntensityPuchasingPower",
    },
    {
      label: "Energy intensity in terms of physical output",
      key: "energyPhysicalOutput",
    },
    { label: "Energy intensity (optional)", key: "energyIntensityOptional" },
  ];

  const wasteRecycledLabels = {
    headings: [
      { label: "Parameter", width: "50%" },
      { label: "Current Financial Year", width: "25%" },
      { label: "Previous Financial Year", width: "25%" },
    ],
    // unitOptions: [
    //   { value: "", label: "Select Unit" },
    //   { value: "Mt CO2 eq", label: "Mt CO2 eq" },
    //   { value: "unit", label: "Unit" },
    // ],
    placeholder: "Enter Emissions",
  };

  const greenGasLabels = {
    headings: [
      { label: "S. No", width: "10%" },
      { label: "Category", width: "50%" },
      { label: "In Previous Financial Year", width: "20%" },
      { label: "In Current Financial Year", width: "20%" },
    ],
    // unitOptions: [
    //   { value: "", label: "Select Unit" },
    //   { value: "Mt CO2 eq", label: "Mt CO2 eq" },
    //   { value: "unit", label: "Unit" },
    // ],
    placeholder: "Enter Number",
  };

  const energySourceLabels = {
    headings: [
      { label: "Parameter", width: "50%" },
      { label: "Current Financial Year", width: "25%" },
      { label: "Previous Financial Year", width: "25%" },
      // { label: "Unit", width: "10%" },
    ],
    // unitOptions: [
    //   { value: "", label: "Select Unit" },
    //   { value: "Joule", label: "Joule" },
    //   { value: "KiloJoule", label: "KiloJoule" },
    // ],
    placeholder: "Enter Quantity",
  };

  const type = "SectionC";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(
          `/brsr/get?subType=principle6&type=${type}&id=${id}`,
        );
        if (
          response?.data?.data?.year
            ? response?.data?.data?.year < currentYear - 1
            : false || !brsrReview
        )
          setDisabled(true);

        if (
          response &&
          response.data &&
          response.data.data.BRSRSectionC &&
          response.data.data.BRSRSectionC.principle6.essentialIndicators
        ) {
          const essentialIndicators =
            response.data.data.BRSRSectionC.principle6.essentialIndicators;

          const data =
            response.data.data.BRSRSectionC.principle6.essentialIndicators
              .greenHouseGasEmissions;

          const waterDischargeCurrent =
            essentialIndicators.waterDischargeCurrent || {};
          const waterDischargePrevious =
            essentialIndicators.waterDischargePrevious || {};

          const unit =
            essentialIndicators.energyConsumptionIntensity.fromRenewableSource
              .electricityConsumption.currentUnit;

          const unit1 = essentialIndicators.airEmissons.nox.currentUnit;
          setSelectedUnit(unit || "");
          setSelectedUnit1(unit1 || "");

          // Set the form data with the necessary values
          setFormData((prevFormData) => ({
            ...prevFormData,
            energyConsumptionIntensityDoc:
              essentialIndicators?.energyConsumptionIntensityDoc ?? [],
            patSchemeDoc: essentialIndicators?.patSchemeDoc ?? [],
            waterDisclosuresRelatedDoc:
              essentialIndicators?.waterDisclosuresRelatedDoc ?? [],
            waterDischargeDoc: essentialIndicators?.waterDischargeDoc ?? [],
            zeroLiquidDischargeDoc:
              essentialIndicators?.zeroLiquidDischargeDoc ?? [],
            airEmissionDoc: essentialIndicators?.airEmissionDoc ?? [],
            greenHouseGasEmissionsDoc:
              essentialIndicators?.greenHouseGasEmissionsDoc ?? [],
            greenHouseGasBooleanEmissionsDoc:
              essentialIndicators?.greenHouseGasBooleanEmissionsDoc ?? [],
            wasteManagementDoc: essentialIndicators?.wasteManagementDoc ?? [],
            wasterManagmentPracticesDoc:
              essentialIndicators?.wasterManagmentPracticesDoc ?? [],
            environmentalApprovalDoc:
              essentialIndicators?.environmentalApprovalDoc ?? [],
            projectUndertakenByentityDoc:
              essentialIndicators?.projectUndertakenByentityDoc ?? [],
            entityawDoc: essentialIndicators?.entityawDoc ?? [],
            fromRenewableSource: {
              electricityConsumption: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.electricityConsumption
                    .currentFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.electricityConsumption.currentUnit,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.electricityConsumption
                    .previousFinancialYear,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.electricityConsumption.previousUnit,
              },
              fuelConsumption: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.fuelConsumption.currentFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.fuelConsumption.currentUnit,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.fuelConsumption.previousFinancialYear,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.fuelConsumption.previousUnit,
              },
              energyConsumption: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.energyConsumption.currentFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.energyConsumption.currentUnit,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.energyConsumption
                    .previousFinancialYear,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromRenewableSource.energyConsumption.previousUnit,
              },
            },
            fromNonRenewableSource: {
              electricityConsumption: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.electricityConsumption
                    .currentFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.electricityConsumption.currentUnit,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.electricityConsumption
                    .previousFinancialYear,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.electricityConsumption.previousUnit,
              },
              fuelConsumption: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.fuelConsumption
                    .currentFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.fuelConsumption.currentUnit,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.fuelConsumption
                    .previousFinancialYear,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.fuelConsumption.previousUnit,
              },
              energyConsumption: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.energyConsumption
                    .currentFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.energyConsumption.currentUnit,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.energyConsumption
                    .previousFinancialYear,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity
                    .fromNonRenewableSource.energyConsumption.previousUnit,
              },
            },
            energyIntensity: {
              energyIntensity: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensity.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensity.previousFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensity.currentUnit,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensity.previousUnit,
              },
              energyIntensityTurnover: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityTurnover.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityTurnover.previousFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityTurnover.currentUnit,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityTurnover.previousUnit,
              },
              energyIntensityPuchasingPower: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityPuchasingPower.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityPuchasingPower.previousFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityPuchasingPower.currentUnit,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityPuchasingPower.previousUnit,
              },
              energyPhysicalOutput: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyPhysicalOutput.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyPhysicalOutput.previousFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyPhysicalOutput.currentUnit,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyPhysicalOutput.previousUnit,
              },
              energyIntensityOptional: {
                currentFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityOptional.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityOptional.previousFinancialYear,
                currentUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityOptional.currentUnit,
                previousUnit:
                  essentialIndicators.energyConsumptionIntensity.energyIntensity
                    .energyIntensityOptional.previousUnit,
              },
            },
            energyIndependentAssessment:
              essentialIndicators.energyConsumptionIntensity
                .energyIndependentAssessment,
            patSchemeDetails: essentialIndicators.patSchemeDetails,
            waterDisclosuresRelated: {
              ...essentialIndicators.waterDisclosuresRelated,
              externalAgency:
                essentialIndicators.waterDisclosuresRelated.externalAgency,
            },

            waterDischargeCurrent: {
              surfaceWater: {
                dischargeWithoutTreatment:
                  waterDischargeCurrent.surfaceWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargeCurrent.surfaceWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargeCurrent.surfaceWater?.dischargeAfterTreatment ??
                  "",
                unit: waterDischargeCurrent.surfaceWater?.unit ?? "KL",
              },
              groundWater: {
                dischargeWithoutTreatment:
                  waterDischargeCurrent.groundWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargeCurrent.groundWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargeCurrent.groundWater?.dischargeAfterTreatment ??
                  "",
                unit: waterDischargeCurrent.groundWater?.unit ?? "KL",
              },
              producedWater: {
                dischargeWithoutTreatment:
                  waterDischargeCurrent.producedWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargeCurrent.producedWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargeCurrent.producedWater
                    ?.dischargeAfterTreatment ?? "",
                unit: waterDischargeCurrent.producedWater?.unit ?? "KL",
              },
              thirdPartyWater: {
                dischargeWithoutTreatment:
                  waterDischargeCurrent.thirdPartyWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargeCurrent.thirdPartyWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargeCurrent.thirdPartyWater
                    ?.dischargeAfterTreatment ?? "",
                unit: waterDischargeCurrent.thirdPartyWater?.unit ?? "KL",
              },
              other: {
                dischargeWithoutTreatment:
                  waterDischargeCurrent.other?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargeCurrent.other?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargeCurrent.other?.dischargeAfterTreatment ?? "",
                unit: waterDischargeCurrent.other?.unit ?? "KL",
              },
              total: waterDischargeCurrent.total ?? "",
            },
            waterDischargePrevious: {
              surfaceWater: {
                dischargeWithoutTreatment:
                  waterDischargePrevious.surfaceWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargePrevious.surfaceWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargePrevious.surfaceWater
                    ?.dischargeAfterTreatment ?? "",
                unit: waterDischargePrevious.surfaceWater?.unit ?? "KL",
              },
              groundWater: {
                dischargeWithoutTreatment:
                  waterDischargePrevious.groundWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargePrevious.groundWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargePrevious.groundWater?.dischargeAfterTreatment ??
                  "",
                unit: waterDischargePrevious.groundWater?.unit ?? "KL",
              },
              producedWater: {
                dischargeWithoutTreatment:
                  waterDischargePrevious.producedWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargePrevious.producedWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargePrevious.producedWater
                    ?.dischargeAfterTreatment ?? "",
                unit: waterDischargePrevious.producedWater?.unit ?? "KL",
              },
              thirdPartyWater: {
                dischargeWithoutTreatment:
                  waterDischargePrevious.thirdPartyWater
                    ?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargePrevious.thirdPartyWater?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargePrevious.thirdPartyWater
                    ?.dischargeAfterTreatment ?? "",
                unit: waterDischargePrevious.thirdPartyWater?.unit ?? "KL",
              },
              other: {
                dischargeWithoutTreatment:
                  waterDischargePrevious.other?.dischargeWithoutTreatment ?? "",
                treatmentLevel:
                  waterDischargePrevious.other?.treatMentLeval ?? "",
                dischargeAfterTreatment:
                  waterDischargePrevious.other?.dischargeAfterTreatment ?? "",
                unit: waterDischargePrevious.other?.unit ?? "KL",
              },
              total: waterDischargePrevious.total ?? "",
            },
            waterDischargeExternalAgency:
              essentialIndicators.waterDischargeExternalAgency,
            zeroLiquidDischarge: essentialIndicators.zeroLiquidDischarge,
            airEmissons: essentialIndicators.airEmissons,
            airEmissionsOthers:
              Array.isArray(essentialIndicators.airEmissionsOthers) &&
              essentialIndicators.airEmissionsOthers.length > 0
                ? essentialIndicators.airEmissionsOthers
                : [
                    {
                      parameter: "",
                      currentFinancialYear: "",
                      previousFinancialYear: "",
                    },
                  ],
            airEmissionAssessment: essentialIndicators.airEmissionAssessment,
            greenHouseGasEmissions: {
              scope1: data.scope1,
              scope2: data.scope2,
              totalOfScope1and2Intensity: data.totalOfScope1and2Intensity,
              scope1and2emissionPPP: data.scope1and2emissionPPP,
              scope1and2emissionIntensityPhysical:
                data.scope1and2emissionIntensityPhysical,
              scope1and2EmissionIntensity: data.scope1and2EmissionIntensity,
              externalAgency: {
                action: data.externalAgency.action,
                name: data.externalAgency.name,
              },
            },
            // greenHouseGasEmissions: essentialIndicators.greenHouseGasEmissions,
            greenHouseGasBooleanEmissions:
              essentialIndicators.greenHouseGasBooleanEmissions,
            wasteGenerated: {
              plastics: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .plastics.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .plastics.previousFinancialYear,
              },
              eWaste: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .eWaste.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .eWaste.previousFinancialYear,
              },
              bioMediacal: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .bioMediacal.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .bioMediacal.previousFinancialYear,
              },
              construtionAndDemolition: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .construtionAndDemolition.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .construtionAndDemolition.previousFinancialYear,
              },
              batteryWaste: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .batteryWaste.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .batteryWaste.previousFinancialYear,
              },
              radioactiveWaste: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .radioactiveWaste.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .radioactiveWaste.previousFinancialYear,
              },
              otherHazardous: {
                otherHazardous:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .otherHazardous.otherHazardous,
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .otherHazardous.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .otherHazardous.previousFinancialYear,
              },
              otherNonHazardous: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .otherNonHazardous.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteGenerated
                    .otherNonHazardous.previousFinancialYear,
              },
              total:
                essentialIndicators.wasteManagementDetails.wasteGenerated.total,
            },
            wasteIntensity: mapWasteIntensityFields(
              essentialIndicators.wasteManagementDetails?.wasteIntensity,
            ),
            wasteGeneratedCategory: {
              recycled: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails
                    .wasteGeneratedCategory.recycled.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails
                    .wasteGeneratedCategory.recycled.previousFinancialYear,
              },
              reUsed: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails
                    .wasteGeneratedCategory.reUsed.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails
                    .wasteGeneratedCategory.reUsed.previousFinancialYear,
              },
              other: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails
                    .wasteGeneratedCategory.other.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails
                    .wasteGeneratedCategory.other.previousFinancialYear,
              },
              total:
                essentialIndicators.wasteManagementDetails
                  .wasteGeneratedCategory.total,
            },
            wasteDesposed: {
              inceneration: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteDesposed
                    .inceneration.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteDesposed
                    .inceneration.previousFinancialYear,
              },
              lanfilling: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteDesposed
                    .lanfilling.currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteDesposed
                    .lanfilling.previousFinancialYear,
              },
              other: {
                currentFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteDesposed.other
                    .currentFinancialYear,
                previousFinancialYear:
                  essentialIndicators.wasteManagementDetails.wasteDesposed.other
                    .previousFinancialYear,
              },
              total:
                essentialIndicators.wasteManagementDetails.wasteDesposed.total,
            },
            independentAssessment: {
              action:
                essentialIndicators.wasteManagementDetails.independentAssessment
                  .action,
              name: essentialIndicators.wasteManagementDetails
                .independentAssessment.name,
            },
            wasterManagmentPractices:
              essentialIndicators.wasterManagmentPractices,
            environmentalApproval: essentialIndicators.environmentalApproval,
            eiaEnabled: toApplicableLabel(essentialIndicators.eiaEnabled),
            projectUndertakenByentity:
              essentialIndicators.projectUndertakenByentity,
            entityComplain: essentialIndicators.entityComplain ?? false,
            // entityComplainwithEnvironmentLaw:
            //   essentialIndicators.entityComplainwithEnvironmentLaw,
            entityComplainwithEnvironmentLaw:
              essentialIndicators.entityComplainwithEnvironmentLaw?.map(
                (item, index) => ({
                  ...item,
                  serialNumber: index + 1,
                }),
              ) || [
                {
                  serialNumber: 1,
                  specifyLaw: "",
                  details: "",
                  penalties: "",
                  correctiveAction: "",
                },
              ],
          }));
        } else {
          console.error("Essential Indicators data is not available.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [type]);

  const handleInputChange = (fieldPrefix, key, field, value) => {
    let nextValue = value;
    if (
      fieldPrefix === "wasteIntensity" &&
      WASTE_INTENSITY_NUMERIC_ROW_KEYS.includes(key) &&
      (field === "currentFinancialYear" ||
        field === "previousFinancialYear")
    ) {
      nextValue = sanitizeWasteIntensityNumericInput(value);
    }

    setFormData((prevData) => {
      const updatedData = { ...prevData };
      const targetField = updatedData[fieldPrefix][key];

      if (typeof targetField === "object" && targetField !== null) {
        targetField[field] = nextValue;
      } else {
        updatedData[fieldPrefix][key] = nextValue;
      }

      return updatedData;
    });
    setReusableErrors((prevErrors) => {
      const isOptionalWasteIntensity =
        fieldPrefix === "wasteIntensity" && key === "wasteIntensityOptiomal";
      const empty = String(nextValue ?? "").trim() === "";
      const markError = isOptionalWasteIntensity ? false : empty;
      return {
        ...prevErrors,
        [fieldPrefix]: {
          ...prevErrors[fieldPrefix],
          [key]: {
            ...prevErrors[fieldPrefix]?.[key],
            [field]: markError,
          },
        },
      };
    });
  };

  const handleOtherAirEmissionChange = (index, field, value) => {
    setFormData((prev) => {
      const existingRows = Array.isArray(prev.airEmissionsOthers)
        ? [...prev.airEmissionsOthers]
        : [];

      const currentRow = existingRows[index] || {
        parameter: "",
        currentFinancialYear: "",
        previousFinancialYear: "",
      };

      existingRows[index] = {
        ...currentRow,
        [field]: value,
      };

      return {
        ...prev,
        airEmissionsOthers: existingRows,
      };
    });
  };

  const handleAddOtherAirEmissionRow = () => {
    setFormData((prev) => ({
      ...prev,
      airEmissionsOthers: [
        ...(Array.isArray(prev.airEmissionsOthers)
          ? prev.airEmissionsOthers
          : []),
        {
          parameter: "",
          currentFinancialYear: "",
          previousFinancialYear: "",
        },
      ],
    }));
  };

  const handleRemoveOtherAirEmissionRow = (index) => {
    setFormData((prev) => {
      const existingRows = Array.isArray(prev.airEmissionsOthers)
        ? [...prev.airEmissionsOthers]
        : [];

      const updatedRows = existingRows.filter((_, i) => i !== index);

      return {
        ...prev,
        airEmissionsOthers:
          updatedRows.length > 0
            ? updatedRows
            : [
                {
                  parameter: "",
                  currentFinancialYear: "",
                  previousFinancialYear: "",
                },
              ],
      };
    });
  };

  const handleRadioChange = (fieldPath, value) => {
    const keys = fieldPath.split(".");
    const updatedData = { ...formData };
    const isYes = value === "yes" || value === true;

    keys.reduce((acc, key, index) => {
      if (index === keys.length - 1) {
        acc[key] = isYes;
      } else {
        acc[key] = acc[key] || {};
      }
      return acc[key];
    }, updatedData);

    // Clear related fields when "no" is selected
    if (!isYes) {
      switch (fieldPath) {
        case "energyIndependentAssessment.action":
          updatedData.energyIndependentAssessment.name = [""];
          setErrorMessages((prev) => ({
            ...prev,
            energyIndependentAssessment: [],
          }));
          break;

        case "patSchemeDetails.action":
          updatedData.patSchemeDetails.remark = "";
          setValidationErrors((prev) => ({
            ...prev,
            patSchemeDetails: false,
          }));
          break;

        case "waterDisclosuresRelated.externalAgency.action":
          updatedData.waterDisclosuresRelated.externalAgency.name = [""];
          setErrorMessages((prev) => ({
            ...prev,
            waterDisclosuresRelated: [],
          }));
          break;

        case "waterDischargeExternalAgency.action":
          updatedData.waterDischargeExternalAgency.name = [""];
          setErrorMessages((prev) => ({
            ...prev,
            waterDischargeExternalAgency: [],
          }));
          break;

        case "zeroLiquidDischarge.action":
          updatedData.zeroLiquidDischarge.remark = "";
          setValidationErrors((prev) => ({
            ...prev,
            zeroLiquidDischarge: false,
          }));
          break;

        case "airEmissionAssessment.action":
          updatedData.airEmissionAssessment.name = [""];
          setErrorMessages((prev) => ({
            ...prev,
            airEmissionAssessment: [],
          }));
          break;

        case "greenHouseGasEmissions.externalAgency.action":
          updatedData.greenHouseGasEmissions.externalAgency.name = [""];
          setErrorMessages((prev) => ({
            ...prev,
            greenHouseGasEmissions: [],
          }));
          break;

        case "greenHouseGasBooleanEmissions.action":
          updatedData.greenHouseGasBooleanEmissions.remark = "";
          setValidationErrors((prev) => ({
            ...prev,
            greenHouseGasBooleanEmissions: false,
          }));
          break;

        case "independentAssessment.action":
          updatedData.independentAssessment.name = [""];
          setErrorMessages((prev) => ({
            ...prev,
            independentAssessment: [],
          }));
          break;

        default:
          break;
      }
    }

    setFormData(updatedData);
  };

  const handleApplicableChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (value === "No" && field === "eiaEnabled") {
        next.projectUndertakenByentity = [
          { serialNumber: 1, ...emptyProjectUndertakenRow },
        ];
      }
      return next;
    });

    if (value === "No" && field === "eiaEnabled") {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.projectUndertakenByentity;
        return next;
      });
    }
  };

  const handleTextFieldChange2 = (fieldPath, value) => {
    const keys = fieldPath.split(".");
    const updatedData = { ...formData };

    keys.reduce((acc, key, index) => {
      if (index === keys.length - 1) {
        acc[key] = value;
      } else {
        acc[key] = acc[key] || {};
      }
      return acc[key];
    }, updatedData);

    setFormData(updatedData);
    // Clear validation error for the field
    if (keys[1] === "remark") {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        [keys[0]]: false,
      }));
    }
  };

  const handleAddField = (fieldPath) => {
    setFormData((prev) => {
      const keys = fieldPath.split(".");
      const updatedData = { ...prev };

      let target = keys.reduce((acc, key, index) => {
        if (index === keys.length - 1) {
          acc[key] = Array.isArray(acc[key]) ? [...acc[key], ""] : [""]; // Add an empty string
        } else {
          acc[key] = acc[key] || {};
        }
        return acc[key];
      }, updatedData);

      return updatedData;
    });
  };

  const handleRemoveField = (fieldPath, index) => {
    setFormData((prev) => {
      const keys = fieldPath.split(".");
      const updatedData = { ...prev };

      let target = keys.reduce((acc, key, i) => {
        if (i === keys.length - 1) {
          if (Array.isArray(acc[key])) {
            acc[key] = acc[key].filter((_, i) => i !== index); // Remove the specific index
          }
        } else {
          acc[key] = acc[key] || {};
        }
        return acc[key];
      }, updatedData);

      return updatedData;
    });
  };

  const askBeforeDelete = (fieldPath, index) => {
    const keys = fieldPath.split(".");
    const fieldArray = keys.reduce((acc, key) => acc?.[key], formData);

    // Check if the field has content at the index
    if (fieldArray?.[index]?.trim()) {
      setRowToDelete({ fieldPath, index });
      setDialogOpen(true);
    } else {
      // Directly remove the row without asking
      trulyRemoveRow(fieldPath, index);
    }
  };

  const trulyRemoveRow = (fieldPath, index) => {
    setFormData((prev) => {
      const keys = fieldPath.split(".");
      const copy = { ...prev };
      keys.reduce((acc, key, i) => {
        if (i === keys.length - 1 && Array.isArray(acc[key])) {
          acc[key] = acc[key].filter((_, k) => k !== index);
        } else {
          acc[key] = acc[key] ?? {};
        }
        return acc[key];
      }, copy);
      return copy;
    });
  };

  // user pressed **Confirm**
  const confirmRemoveRow1 = () => {
    if (rowToDelete) {
      trulyRemoveRow(rowToDelete.fieldPath, rowToDelete.index); // <- old logic
    }
    setDialogOpen(false);
    setRowToDelete(null);
  };

  // user pressed **Cancel** / closed the dialog
  const cancelRemoveRow1 = () => {
    setDialogOpen(false);
    setRowToDelete(null);
  };

  const handleTextFieldChange = (fieldPath, index, value) => {
    setFormData((prev) => {
      const keys = fieldPath.split(".");
      const updatedData = { ...prev };

      let target = keys.reduce((acc, key, i) => {
        if (i === keys.length - 1) {
          if (Array.isArray(acc[key])) {
            acc[key] = [...acc[key]];
            acc[key][index] = value;
          }
        } else {
          acc[key] = acc[key] || {};
        }
        return acc[key];
      }, updatedData);

      return updatedData;
    });

    // Clear error for this specific field
    setErrorMessages((prevErrors) => {
      const fieldKey = fieldPath.split(".")[0]; // Extract the field key (e.g., "waterDischargeExternalAgency")
      const updatedErrors = { ...prevErrors };
      updatedErrors[fieldKey] = updatedErrors[fieldKey] || []; // Ensure the field is initialized as an array
      updatedErrors[fieldKey][index] =
        value.trim() === "" ? "This field is required" : "";
      return updatedErrors;
    });
  };

  const calculateTotalDischarge = (type) => {
    return parameters.reduce((total, parameter) => {
      const withoutTreatment =
        parseFloat(formData[type][parameter]?.dischargeWithoutTreatment) ?? 0;
      const withTreatment =
        parseFloat(formData[type][parameter]?.dischargeAfterTreatment) ?? 0;
      return total + withoutTreatment + withTreatment;
    }, 0);
  };

  const handleTextFieldChange1 = (path, value) => {
    setFormData((prevData) => {
      const updatedData = { ...prevData };
      const keys = path.split(".");
      let temp = updatedData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!temp[keys[i]]) temp[keys[i]] = {}; // Ensure nested object exists
        temp = temp[keys[i]];
      }

      temp[keys[keys.length - 1]] = value;
      return { ...updatedData };
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newErrorMessages = { ...errorMessages };

    if (formData.energyIndependentAssessment.action) {
      newErrorMessages.energyIndependentAssessment =
        formData.energyIndependentAssessment.name.map((name) =>
          name.trim() === "" ? "This field is required" : "",
        );
      if (
        newErrorMessages.energyIndependentAssessment.some(
          (error) => error !== "",
        )
      ) {
        isValid = false;
      }
    }

    if (formData.airEmissionAssessment.action) {
      newErrorMessages.airEmissionAssessment =
        formData.airEmissionAssessment.name.map((name) =>
          name.trim() === "" ? "This field is required" : "",
        );
      if (
        newErrorMessages.airEmissionAssessment.some((error) => error !== "")
      ) {
        isValid = false;
      }
    }

    if (formData.waterDischargeExternalAgency.action) {
      newErrorMessages.waterDischargeExternalAgency =
        formData.waterDischargeExternalAgency.name.map((name) =>
          name.trim() === "" ? "This field is required" : "",
        );
      if (
        newErrorMessages.waterDischargeExternalAgency.some(
          (error) => error !== "",
        )
      ) {
        isValid = false;
      }
    }

    if (formData.independentAssessment.action) {
      newErrorMessages.independentAssessment =
        formData.independentAssessment.name.map((name) =>
          name.trim() === "" ? "This field is required" : "",
        );
      if (
        newErrorMessages.independentAssessment.some((error) => error !== "")
      ) {
        isValid = false;
      }
    }

    if (formData.waterDisclosuresRelated.externalAgency.action) {
      newErrorMessages.waterDisclosuresRelated.externalAgency =
        formData.waterDisclosuresRelated.externalAgency.name.map((name) =>
          name.trim() === "" ? "This field is required" : "",
        );
      if (
        newErrorMessages.waterDisclosuresRelated.externalAgency.some(
          (error) => error !== "",
        )
      ) {
        isValid = false;
      }
    }

    if (formData.greenHouseGasEmissions.externalAgency.action) {
      newErrorMessages.greenHouseGasEmissions.externalAgency =
        formData.greenHouseGasEmissions.externalAgency.name.map((name) =>
          name.trim() === "" ? "This field is required" : "",
        );
      if (
        newErrorMessages.greenHouseGasEmissions.externalAgency.some(
          (error) => error !== "",
        )
      ) {
        isValid = false;
      }
    }

    setErrorMessages(newErrorMessages);
    return isValid;
  };

  const validateFormField = () => {
    let errors = {
      patSchemeDetails: false,
      zeroLiquidDischarge: false,
      greenHouseGasBooleanEmissions: false,
    };

    if (formData.patSchemeDetails.action && !formData.patSchemeDetails.remark) {
      errors.patSchemeDetails = true;
    }

    if (
      formData.zeroLiquidDischarge.action &&
      !formData.zeroLiquidDischarge.remark
    ) {
      errors.zeroLiquidDischarge = true;
    }

    if (
      formData.greenHouseGasBooleanEmissions.action &&
      !formData.greenHouseGasBooleanEmissions.remark
    ) {
      errors.greenHouseGasBooleanEmissions = true;
    }

    if (!formData.wasterManagmentPractices.trim()) {
      errors.wasterManagmentPractices = "This field is required";
    }

    setValidationErrors(errors); // Update validation errors state

    // Return true if there are no errors, false otherwise
    return !Object.values(errors).some((error) => error);
  };

  const validateTable = () => {
    let newErrors = {};

    console.log(formData.entityComplain, "formData.entityComplain");

    // ✅ Validate `environmentalApproval` (Row-Based Validation)
    if (
      formData.entityComplain === "yes" ||
      formData.entityComplain === true ||
      formData.entityComplain === "true"
    ) {
      formData.environmentalApproval.forEach((assessment, rowIndex) => {
        Object.entries(assessment).forEach(([key, value]) => {
          if (value === "" || value === null) {
            if (!newErrors.environmentalApproval)
              newErrors.environmentalApproval = [];
            newErrors.environmentalApproval[rowIndex] = {
              ...(newErrors.environmentalApproval[rowIndex] || {}),
              [key]: ` ${key}`, // ✅ Includes Row Number
              // [key]: `Row ${rowIndex + 1}: ${key}`,
            };
          }
        });
      });
    }
    // ✅ Validate `projectUndertakenByentity` (Row-Based Validation)
    if (isApplicableYes(formData.eiaEnabled)) {
      formData.projectUndertakenByentity.forEach((item, rowIndex) => {
        Object.entries(item).forEach(([key, value]) => {
          if (value === "" || value === null) {
            if (!newErrors.projectUndertakenByentity)
              newErrors.projectUndertakenByentity = [];
            newErrors.projectUndertakenByentity[rowIndex] = {
              ...(newErrors.projectUndertakenByentity[rowIndex] || {}),
              [key]: ` ${key}`,
            };
          }
        });
      });
    }

    // ✅ Validate `entityComplainwithEnvironmentLaw` (Row-Based Validation)
    // formData.entityComplainwithEnvironmentLaw.forEach((item, rowIndex) => {
    //   Object.entries(item).forEach(([key, value]) => {
    //     if (value === "" || value === null) {
    //       if (!newErrors.entityComplainwithEnvironmentLaw)
    //         newErrors.entityComplainwithEnvironmentLaw = [];
    //       newErrors.entityComplainwithEnvironmentLaw[rowIndex] = {
    //         ...(newErrors.entityComplainwithEnvironmentLaw[rowIndex] || {}),
    //         [key]: ` ${key}`,
    //       };
    //     }
    //   });
    // });

    if (
      formData.entityComplain === "yes" ||
      formData.entityComplain === true ||
      formData.entityComplain === "true"
    ) {
      formData.entityComplainwithEnvironmentLaw.forEach((item, rowIndex) => {
        Object.entries(item).forEach(([key, value]) => {
          if (value === "" || value === null) {
            if (!newErrors.entityComplainwithEnvironmentLaw)
              newErrors.entityComplainwithEnvironmentLaw = [];
            newErrors.entityComplainwithEnvironmentLaw[rowIndex] = {
              ...(newErrors.entityComplainwithEnvironmentLaw[rowIndex] || {}),
              [key]: ` ${key}`,
            };
          }
        });
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // ✅ Returns `true` if no errors
  };

  const validateReusableFields = () => {
    let isValid = true;
    const newErrors = JSON.parse(JSON.stringify(reusableErrors)); // Clone initial state

    // Renewable sources
    Object.keys(formData.fromRenewableSource).forEach((key) => {
      const field = formData.fromRenewableSource[key];
      newErrors.fromRenewableSource[key].currentFinancialYear = !String(
        field.currentFinancialYear,
      ).trim();
      newErrors.fromRenewableSource[key].previousFinancialYear = !String(
        field.previousFinancialYear,
      ).trim();

      if (
        newErrors.fromRenewableSource[key].currentFinancialYear ||
        newErrors.fromRenewableSource[key].previousFinancialYear
      ) {
        isValid = false;
      }
    });

    Object.keys(formData.fromNonRenewableSource).forEach((key) => {
      const field = formData.fromNonRenewableSource[key];
      newErrors.fromNonRenewableSource[key].currentFinancialYear = !String(
        field.currentFinancialYear,
      ).trim();
      newErrors.fromNonRenewableSource[key].previousFinancialYear = !String(
        field.previousFinancialYear,
      ).trim();

      if (
        newErrors.fromNonRenewableSource[key].currentFinancialYear ||
        newErrors.fromNonRenewableSource[key].previousFinancialYear
      ) {
        isValid = false;
      }
    });

    Object.keys(formData.energyIntensity).forEach((key) => {
      const field = formData.energyIntensity[key];
      if (key === "energyIntensityOptional") {
        newErrors.energyIntensity[key].currentFinancialYear = "";
        newErrors.energyIntensity[key].previousFinancialYear = "";
        return;
      }
      newErrors.energyIntensity[key].currentFinancialYear = !String(
        field.currentFinancialYear,
      ).trim();
      newErrors.energyIntensity[key].previousFinancialYear = !String(
        field.previousFinancialYear,
      ).trim();

      if (
        newErrors.energyIntensity[key].currentFinancialYear ||
        newErrors.energyIntensity[key].previousFinancialYear
      ) {
        isValid = false;
      }
    });

    // Object.keys(formData.wasteGenerated).forEach((key) => {
    //   // Skip validation for total if it's calculated
    //   if (key === "total") return;
    //   const field = formData.wasteGenerated[key];
    //   if (newErrors.wasteGenerated[key]) {
    //     // Add safety check
    //     newErrors.wasteGenerated[key].currentFinancialYear = !String(
    //       field.currentFinancialYear
    //     ).trim();
    //     newErrors.wasteGenerated[key].previousFinancialYear = !String(
    //       field.previousFinancialYear
    //     ).trim();

    //     if (
    //       newErrors.wasteGenerated[key].currentFinancialYear ||
    //       newErrors.wasteGenerated[key].previousFinancialYear
    //     ) {
    //       isValid = false;
    //     }
    //   }
    // });
    Object.keys(formData.wasteGenerated).forEach((key) => {
      if (key === "total") return; // Skip total row

      const field = formData.wasteGenerated[key];

      if (!newErrors.wasteGenerated[key]) {
        newErrors.wasteGenerated[key] = {};
      }

      const isEmpty = (val) =>
        val === null || val === undefined || String(val).trim() === "";
      if (!isEmpty(field.otherHazardous) && key === "otherHazardous") {
        // If Other Specify is empty, validate years
        newErrors.wasteGenerated[key].currentFinancialYear = isEmpty(
          field.currentFinancialYear,
        );
        newErrors.wasteGenerated[key].previousFinancialYear = isEmpty(
          field.previousFinancialYear,
        );
        if (
          newErrors.wasteGenerated[key].currentFinancialYear ||
          newErrors.wasteGenerated[key].previousFinancialYear
        ) {
          isValid = false;
        }
      } else if (key !== "otherNonHazardous" && key !== "otherHazardous") {
        // validate only years
        newErrors.wasteGenerated[key].currentFinancialYear = isEmpty(
          field.currentFinancialYear,
        );
        newErrors.wasteGenerated[key].previousFinancialYear = isEmpty(
          field.previousFinancialYear,
        );

        // if (
        //   newErrors.wasteGenerated[key].currentFinancialYear ||
        //   newErrors.wasteGenerated[key].previousFinancialYear
        // ) {
        //   isValid = false;
        // }
      }
    });

    Object.keys(formData.wasteIntensity).forEach((key) => {
      if (key === "wasteIntensityOptiomal") {
        newErrors.wasteIntensity[key].currentFinancialYear = false;
        newErrors.wasteIntensity[key].previousFinancialYear = false;
        return;
      }
      const field = formData.wasteIntensity[key];
      newErrors.wasteIntensity[key].currentFinancialYear =
        !isWasteIntensityMandatoryNumber(field.currentFinancialYear);
      newErrors.wasteIntensity[key].previousFinancialYear =
        !isWasteIntensityMandatoryNumber(field.previousFinancialYear);

      if (
        newErrors.wasteIntensity[key].currentFinancialYear ||
        newErrors.wasteIntensity[key].previousFinancialYear
      ) {
        isValid = false;
      }
    });

    Object.keys(formData.wasteDesposed).forEach((key) => {
      // Skip validation for total if it's calculated
      if (key === "total") return;

      const field = formData.wasteDesposed[key];
      if (newErrors.wasteDesposed[key]) {
        // Add safety check
        newErrors.wasteDesposed[key].currentFinancialYear = !String(
          field.currentFinancialYear,
        ).trim();
        newErrors.wasteDesposed[key].previousFinancialYear = !String(
          field.previousFinancialYear,
        ).trim();

        if (
          newErrors.wasteDesposed[key].currentFinancialYear ||
          newErrors.wasteDesposed[key].previousFinancialYear
        ) {
          isValid = false;
        }
      }
    });

    Object.keys(formData.wasteGeneratedCategory).forEach((key) => {
      // Skip validation for total if it's calculated
      if (key === "total") return;

      const field = formData.wasteGeneratedCategory[key];
      if (newErrors.wasteGeneratedCategory[key]) {
        // Add safety check
        newErrors.wasteGeneratedCategory[key].currentFinancialYear = !String(
          field.currentFinancialYear,
        ).trim();
        newErrors.wasteGeneratedCategory[key].previousFinancialYear = !String(
          field.previousFinancialYear,
        ).trim();

        if (
          newErrors.wasteGeneratedCategory[key].currentFinancialYear ||
          newErrors.wasteGeneratedCategory[key].previousFinancialYear
        ) {
          isValid = false;
        }
      }
    });

    Object.keys(formData.airEmissons).forEach((key) => {
      // Skip validation for total if it's calculated
      if (key === "total") return;

      const field = formData.airEmissons[key];
      if (newErrors.airEmissons[key]) {
        // Add safety check
        newErrors.airEmissons[key].currentFinancialYear = !String(
          field.currentFinancialYear,
        ).trim();
        newErrors.airEmissons[key].previousFinancialYear = !String(
          field.previousFinancialYear,
        ).trim();

        if (
          newErrors.airEmissons[key].currentFinancialYear ||
          newErrors.airEmissons[key].previousFinancialYear
        ) {
          isValid = false;
        }
      }
    });

    Object.keys(formData.greenHouseGasEmissions).forEach((key) => {
      // Skip validation for total if it's calculated
      if (key === "total") return;

      const field = formData.greenHouseGasEmissions[key];
      if (newErrors.greenHouseGasEmissions[key]) {
        // Add safety check
        newErrors.greenHouseGasEmissions[key].currentFinancialYear = !String(
          field.currentFinancialYear,
        ).trim();
        newErrors.greenHouseGasEmissions[key].previousFinancialYear = !String(
          field.previousFinancialYear,
        ).trim();

        if (
          newErrors.greenHouseGasEmissions[key].currentFinancialYear ||
          newErrors.greenHouseGasEmissions[key].previousFinancialYear
        ) {
          isValid = false;
        }
      }
    });
    // Repeat similar logic for non-renewable and energy intensity
    // ...

    setReusableErrors(newErrors);
    return isValid;
  };

  const validateWaterDischarge = () => {
    let isValid = true;
    const newErrors = JSON.parse(JSON.stringify(waterDischargeErrors));

    parameters.forEach((parameter) => {
      // Current Financial Year Validation
      const current = formData.waterDischargeCurrent[parameter];
      newErrors.current[parameter].dischargeWithoutTreatment =
        !current?.dischargeWithoutTreatment?.trim();
      newErrors.current[parameter].dischargeAfterTreatment =
        !current?.dischargeAfterTreatment?.trim();
      newErrors.current[parameter].treatmentLevel =
        current?.dischargeAfterTreatment?.trim() &&
        !current?.treatmentLevel?.trim();

      // Previous Financial Year Validation
      const previous = formData.waterDischargePrevious[parameter];
      newErrors.previous[parameter].dischargeWithoutTreatment =
        !previous?.dischargeWithoutTreatment?.trim();
      newErrors.previous[parameter].dischargeAfterTreatment =
        !previous?.dischargeAfterTreatment?.trim();
      newErrors.previous[parameter].treatmentLevel =
        previous?.dischargeAfterTreatment?.trim() &&
        !previous?.treatmentLevel?.trim();

      // Check if any errors exist
      if (
        Object.values(newErrors.current[parameter]).some(Boolean) ||
        Object.values(newErrors.previous[parameter]).some(Boolean)
      ) {
        isValid = false;
      }
    });

    setWaterDischargeErrors(newErrors);
    console.log(newErrors, "newErrors");
    return isValid;
  };

  const validateUnits = () => {
    const newUnitErrors = {
      selectedUnit: !selectedUnit || selectedUnit.trim() === "",
      selectedUnit1: !selectedUnit1 || selectedUnit1.trim() === "",
    };
    setUnitErrors(newUnitErrors);
    return !newUnitErrors.selectedUnit && !newUnitErrors.selectedUnit1;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      setIsSubmitted(true);
      const isFormValid = validateForm();
      const isTableValid = validateTable();
      const isValidateFormField = validateFormField();
      const isValidateReusableFields = validateReusableFields();
      const isWaterValid = validateWaterDischarge();
      const isUnitsValid = validateUnits();

      console.log(
        isFormValid,
        isTableValid,
        isValidateFormField,
        isValidateReusableFields,
        isWaterValid,
        isUnitsValid,
        "isFormValid,isTableValid,isValidateFormField,isValidateReusableFields,isWaterValid,isUnitsValid",
      );

      if (
        !isFormValid ||
        !isTableValid ||
        !isValidateFormField ||
        !isValidateReusableFields ||
        !isWaterValid ||
        !isUnitsValid
      ) {
        return;
      }
      const { wasteGenerated, wasteGeneratedCategory, wasteDesposed } =
        formData;

      const updatedFormData = {
        ...formData,
        fromRenewableSource: {
          ...formData.fromRenewableSource,
          electricityConsumption: {
            ...formData.fromRenewableSource.electricityConsumption,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          fuelConsumption: {
            ...formData.fromRenewableSource.fuelConsumption,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          energyConsumption: {
            ...formData.fromRenewableSource.energyConsumption,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
        },
        fromNonRenewableSource: {
          ...formData.fromNonRenewableSource,
          electricityConsumption: {
            ...formData.fromNonRenewableSource.electricityConsumption,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          fuelConsumption: {
            ...formData.fromNonRenewableSource.fuelConsumption,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          energyConsumption: {
            ...formData.fromNonRenewableSource.energyConsumption,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
        },
        energyIntensity: {
          ...formData.energyIntensity,
          energyIntensity: {
            ...formData.energyIntensity.energyIntensity,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          energyIntensityTurnover: {
            ...formData.energyIntensity.energyIntensityTurnover,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          energyIntensityPuchasingPower: {
            ...formData.energyIntensity.energyIntensityPuchasingPower,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          energyPhysicalOutput: {
            ...formData.energyIntensity.energyPhysicalOutput,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
          energyIntensityOptional: {
            ...formData.energyIntensity.energyIntensityOptional,
            currentUnit: selectedUnit,
            previousUnit: selectedUnit,
          },
        },
      };

      const formattedPayload = {
        surfaceWater: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.surfaceWater
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.surfaceWater
                .previousFinancialYear,
            ) || 0,
        },
        groundWater: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.groundWater.currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.groundWater
                .previousFinancialYear,
            ) || 0,
        },
        thirdPartyWater: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.thirdPartyWater
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.thirdPartyWater
                .previousFinancialYear,
            ) || 0,
        },
        seaWater: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.seaWater.currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.seaWater.previousFinancialYear,
            ) || 0,
        },
        others: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.others.currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.others.previousFinancialYear,
            ) || 0,
        },
        totalVolumnWithdrawal: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.totalVolumnWithdrawal
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.totalVolumnWithdrawal
                .previousFinancialYear,
            ) || 0,
        },
        totalWaterConsumption: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.totalWaterConsumption
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.totalWaterConsumption
                .previousFinancialYear,
            ) || 0,
        },
        waterIntensityTurnover: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityTurnover
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityTurnover
                .previousFinancialYear,
            ) || 0,
        },
        waterIntensityPuchasingPower: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityPuchasingPower
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityPuchasingPower
                .previousFinancialYear,
            ) || 0,
        },
        waterIntensityPhyiscalOutput: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityPhyiscalOutput
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityPhyiscalOutput
                .previousFinancialYear,
            ) || 0,
        },
        waterIntensityOptional: {
          currentFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityOptional
                .currentFinancialYear,
            ) || 0,
          previousFinancialYear:
            Number(
              formData.waterDisclosuresRelated.waterIntensityOptional
                .previousFinancialYear,
            ) || 0,
        },
      };

      //6
      const updatedAirEmissions = Object.keys(formData.airEmissons).reduce(
        (acc, key) => {
          if (typeof formData.airEmissons[key] === "object") {
            acc[key] = {
              ...formData.airEmissons[key],
              currentUnit: selectedUnit1, // Apply selected unit
              previousUnit: selectedUnit1, // Apply selected unit
            };
          } else {
            acc[key] = formData.airEmissons[key];
          }
          return acc;
        },
        {},
      );

      const payload = {
        essentialIndicators: {
          ...formData,

          energyConsumptionIntensity: {
            ...updatedFormData,
            energyIndependentAssessment: formData.energyIndependentAssessment,
          },
          patSchemeDetails: formData.patSchemeDetails,
          //3
          waterDisclosuresRelated: {
            ...formattedPayload,
            externalAgency: formData.waterDisclosuresRelated.externalAgency,
          },
          //4
          waterDischargeCurrent: {
            surfaceWater: {
              dischargeWithoutTreatment:
                formData.waterDischargeCurrent.surfaceWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargeCurrent.surfaceWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargeCurrent.surfaceWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargeCurrent.surfaceWater.unit,
            },
            groundWater: {
              dischargeWithoutTreatment:
                formData.waterDischargeCurrent.groundWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargeCurrent.groundWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargeCurrent.groundWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargeCurrent.groundWater.unit,
            },
            producedWater: {
              dischargeWithoutTreatment:
                formData.waterDischargeCurrent.producedWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargeCurrent.producedWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargeCurrent.producedWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargeCurrent.producedWater.unit,
            },
            thirdPartyWater: {
              dischargeWithoutTreatment:
                formData.waterDischargeCurrent.thirdPartyWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargeCurrent.thirdPartyWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargeCurrent.thirdPartyWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargeCurrent.thirdPartyWater.unit,
            },
            other: {
              dischargeWithoutTreatment:
                formData.waterDischargeCurrent.other.dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargeCurrent.other.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargeCurrent.other.dischargeAfterTreatment,
              unit: formData.waterDischargeCurrent.other.unit,
            },
            total: calculateTotalDischarge("waterDischargeCurrent").toString(),
          },

          waterDischargePrevious: {
            surfaceWater: {
              dischargeWithoutTreatment:
                formData.waterDischargePrevious.surfaceWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargePrevious.surfaceWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargePrevious.surfaceWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargePrevious.surfaceWater.unit,
            },
            groundWater: {
              dischargeWithoutTreatment:
                formData.waterDischargePrevious.groundWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargePrevious.groundWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargePrevious.groundWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargePrevious.groundWater.unit,
            },
            producedWater: {
              dischargeWithoutTreatment:
                formData.waterDischargePrevious.producedWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargePrevious.producedWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargePrevious.producedWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargePrevious.producedWater.unit,
            },
            thirdPartyWater: {
              dischargeWithoutTreatment:
                formData.waterDischargePrevious.thirdPartyWater
                  .dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargePrevious.thirdPartyWater.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargePrevious.thirdPartyWater
                  .dischargeAfterTreatment,
              unit: formData.waterDischargePrevious.thirdPartyWater.unit,
            },
            other: {
              dischargeWithoutTreatment:
                formData.waterDischargePrevious.other.dischargeWithoutTreatment,
              treatMentLeval:
                formData.waterDischargePrevious.other.treatmentLevel,
              dischargeAfterTreatment:
                formData.waterDischargePrevious.other.dischargeAfterTreatment,
              unit: formData.waterDischargePrevious.other.unit,
            },
            total: calculateTotalDischarge("waterDischargePrevious").toString(),
          },
          waterDischargeExternalAgency: formData.waterDischargeExternalAgency,
          zeroLiquidDischarge: formData.zeroLiquidDischarge,
          //6
          airEmissons: updatedAirEmissions,
          airEmissionAssessment: formData.airEmissionAssessment,
          //7
          greenHouseGasEmissions: {
            scope1: { ...formData.greenHouseGasEmissions.scope1 },
            scope2: { ...formData.greenHouseGasEmissions.scope2 },
            totalOfScope1and2Intensity: {
              ...formData.greenHouseGasEmissions.totalOfScope1and2Intensity,
            },
            scope1and2emissionPPP: {
              ...formData.greenHouseGasEmissions.scope1and2emissionPPP,
            },
            scope1and2emissionIntensityPhysical: {
              ...formData.greenHouseGasEmissions
                .scope1and2emissionIntensityPhysical,
            },
            scope1and2EmissionIntensity: {
              ...formData.greenHouseGasEmissions.scope1and2EmissionIntensity,
            },
            externalAgency: formData.greenHouseGasEmissions.externalAgency,
          },
          greenHouseGasBooleanEmissions: formData.greenHouseGasBooleanEmissions,
          //9
          wasteManagementDetails: {
            wasteGenerated,
            wasteIntensity: mapWasteIntensityFields(formData.wasteIntensity),
            wasteGeneratedCategory,
            wasteDesposed,
            independentAssessment: formData.independentAssessment,
          },
          wasterManagmentPractices: formData.wasterManagmentPractices,
          //13
          entityComplain: formData.entityComplain,
          //11
          environmentalApproval: formData.environmentalApproval.map(
            ({ serialNumber, ...rest }) => rest,
          ),
          eiaEnabled: formData.eiaEnabled === "Yes",
          projectUndertakenByentity: isApplicableYes(formData.eiaEnabled)
            ? formData.projectUndertakenByentity.map(
                ({ serialNumber, ...rest }) => rest,
              )
            : [],
          entityComplainwithEnvironmentLaw:
            formData.entityComplainwithEnvironmentLaw.map(
              ({ serialNumber, ...rest }) => rest,
            ),
        },
        ...modalupload,
        essentialIndicatorsSubmitted: true,
        type: "SectionC",
        subType: "principle6",
      };

      try {
        const response = await put(`/brsr/update?id=${id}`, payload);
        setsnackOpen({
          open: true,
          message: "Data saved successfully!",
          severity: "success",
        });
        if (onSave) onSave(response.data);
      } catch (error) {
        console.error(error);
        setsnackOpen({
          open: true,
          message: "Failed to save data. Please try again.",
          severity: "error",
        });
      }
    } else onSave();
  };

  const tablestyles = {
    headerCell: {
      color: "#fff",
      fontWeight: "bold",
      textAlign: "center",
      borderRight: "1px solid #fff",
    },
    bodyCell: {
      padding: "6px 16px",
      border: "1px solid rgba(224, 224, 224, 1)",
    },
    textField: {
      size: "small",
      fullWidth: true,
      variant: "standard",
      InputProps: { disableUnderline: true, className: "textField" },
    },
  };

  const parameters = [
    "surfaceWater",
    "groundWater",
    "producedWater",
    "thirdPartyWater",
    "other",
  ];

  const parameterDisplayMap = {
    surfaceWater: "To Surface Water",
    groundWater: "To Groundwater",
    producedWater: "To Seawater",
    thirdPartyWater: "Sent to Third Parties",
    other: "Others",
  };

  const getWaterDischargeErrorMessages = () => {
    const messages = [];
    parameters.forEach((parameter) => {
      // Current year errors
      const current = waterDischargeErrors.current[parameter];
      if (current.dischargeWithoutTreatment) {
        messages.push(
          `${parameterDisplayMap[parameter]} - Current year not treated discharge is required`,
        );
      }
      if (current.dischargeAfterTreatment) {
        messages.push(
          `${parameterDisplayMap[parameter]} - Current year treated discharge is required`,
        );
      }
      if (current.treatmentLevel) {
        messages.push(
          `${parameterDisplayMap[parameter]} - Treatment level is required when discharging after treatment (Current year)`,
        );
      }

      // Previous year errors
      const previous = waterDischargeErrors.previous[parameter];
      if (previous.dischargeWithoutTreatment) {
        messages.push(
          `${parameterDisplayMap[parameter]} - Previous year not treated discharge is required`,
        );
      }
      if (previous.dischargeAfterTreatment) {
        messages.push(
          `${parameterDisplayMap[parameter]} - Previous year treated discharge is required`,
        );
      }
      if (previous.treatmentLevel) {
        messages.push(
          `${parameterDisplayMap[parameter]} - Treatment level is required when discharging after treatment (Previous year)`,
        );
      }
    });
    return messages;
  };

  return (
    <>
      <p className={style.subHeading}>I. ESSENTIAL INDICATOR</p>
      <Box>
        <DeleteRowDialog
          openDialog={dialogOpen}
          cancelRemoveRow={cancelRemoveRow1}
          confirmRemoveRow={confirmRemoveRow1}
        />
        <Box sx={{ mb: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10rem" }}>
            <p className={style.question_title}>
              1. Details of total energy consumption (in Joules or multiples)
              and energy intensity.
            </p>
            {/* Dropdown */}
            <FormControl
              size="small"
              variant="outlined"
              error={isSubmitted && unitErrors.selectedUnit}
              sx={{ minWidth: 150, marginBottom: "1.8rem" }}
            >
              <Select
                value={selectedUnit}
                disabled={disabled}
                onChange={(e) => {
                  setSelectedUnit(e.target.value);
                  if (e.target.value) {
                    setUnitErrors((prev) => ({ ...prev, selectedUnit: false }));
                  }
                }}
                displayEmpty
                sx={{
                  height: "34px",
                  fontSize: "14px",
                  padding: "2px 10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      isSubmitted && unitErrors.selectedUnit
                        ? "#d32f2f"
                        : "#ccc",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Unit
                </MenuItem>
                <MenuItem value="MJ">MJ</MenuItem>
                <MenuItem value="GJ">GJ</MenuItem>
                <MenuItem value="TJ">TJ</MenuItem>
              </Select>
              {isSubmitted && unitErrors.selectedUnit && (
                <FormHelperText
                  sx={{
                    color: "#d32f2f",
                    marginTop: "4px",
                    fontSize: "12px",
                    marginLeft: "0rem",
                  }}
                >
                  Unit is required
                </FormHelperText>
              )}
            </FormControl>
          </div>

          <Box sx={{ marginLeft: "1rem", marginTop: "-0.5rem" }}>
            <p className={style.question_title}>a. From Renewable Sources</p>
            <ReusableTable
              disabled={disabled}
              data={energySource}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="fromRenewableSource"
              labels={energySourceLabels}
              energySource={energySource}
              energySourceNonRenewable={energySourceNonRenewable}
              reusableErrors={reusableErrors}
            />

            <p className={style.question_title} style={{ marginTop: "1.5rem" }}>
              b. From Non-Renewable Sources
            </p>
            <ReusableTable
              disabled={disabled}
              data={energySourceNonRenewable}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="fromNonRenewableSource"
              labels={energySourceLabels}
              energySource={energySource}
              energySourceNonRenewable={energySourceNonRenewable}
              reusableErrors={reusableErrors}
            />
            {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
            <p className={style.question_title} style={{ marginTop: "1.5rem" }}>
              c. Energy Intensity
            </p>
            <ReusableTable
              disabled={disabled}
              data={energyIntensity}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="energyIntensity"
              labels={energySourceLabels}
              isEnergyIntensity={true}
              energySource={energySource}
              energySourceNonRenewable={energySourceNonRenewable}
              reusableErrors={reusableErrors}
            />
          </Box>

          <p style={{ "text-indent": 0 }} className={style.question_title}>
            Indicate if any independent assessment/ evaluation/assurance has
            been carried out by an external agency? If yes, name of the external
            agency.
          </p>
          <Box sx={{ margin: "0.5rem 0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="energyIndependentAssessment.action"
              value={formData.energyIndependentAssessment.action}
              onChange={(e) =>
                handleRadioChange(
                  "energyIndependentAssessment.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>
          {formData.energyIndependentAssessment.action && (
            <Box sx={{ marginLeft: "1rem", marginTop: "-0.8rem" }}>
              <p className={style.question_title}>Name of External Agency</p>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {formData.energyIndependentAssessment.name?.map(
                  (name, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <TextField
                        disabled={disabled}
                        size="small"
                        sx={{ width: "26rem", marginTop: "-0.8rem" }}
                        placeholder="Enter Name Here"
                        // value={formData.energyIndependentAssessment.name}
                        value={name}
                        multiline
                        onChange={(e) =>
                          handleTextFieldChange(
                            "energyIndependentAssessment.name",
                            index,
                            e.target.value,
                          )
                        }
                        error={
                          !!errorMessages.energyIndependentAssessment?.[index]
                        }
                        helperText={
                          <Typography
                            variant="body2"
                            color="error"
                            style={{
                              lineHeight: "1.2",
                              marginLeft: "-1rem",
                              color: "#d34040",
                              fontSize: "10px",
                              fontStyle: "italic",
                            }}
                          >
                            {errorMessages.energyIndependentAssessment?.[
                              index
                            ] || ""}
                          </Typography>
                        }
                      />
                      {formData.energyIndependentAssessment.name.length > 1 && (
                        <IconButton
                          disabled={disabled}
                          // onClick={() =>
                          //   handleRemoveField(
                          //     "energyIndependentAssessment.name",
                          //     index
                          //   )
                          // }
                          onClick={() =>
                            askBeforeDelete(
                              "energyIndependentAssessment.name",
                              index,
                            )
                          }
                        >
                          <Delete color="error" />
                        </IconButton>
                      )}
                    </Box>
                  ),
                )}

                <Button
                  // disabled={isFilleds || false}
                  startIcon={<Add />}
                  onClick={() =>
                    handleAddField("energyIndependentAssessment.name")
                  }
                  disabled={
                    formData.energyIndependentAssessment.name[
                      formData.energyIndependentAssessment.name.length - 1
                    ] === "" || disabled
                  }
                  sx={{
                    color: "#1C486B",
                    width: "9rem",
                    borderColor: "#1C486B",
                    marginTop: "-0.8rem",
                    height: "2.5rem",
                  }}
                  variant="outlined"
                >
                  Add Field
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "energyConsumptionIntensityDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title}>
            2. Does the entity have any sites / facilities identified as
            designated consumers (DCs) under the Performance, Achieve and Trade
            (PAT) Scheme of the Government of India? If yes, disclose whether
            targets set under the PAT scheme have been achieved. In case targets
            have not been achieved, provide the remedial action taken, if any.
          </p>
          <Box sx={{ margin: "0.5rem 0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="patSchemeDetails.action"
              value={formData.patSchemeDetails.action}
              onChange={(e) =>
                handleRadioChange("patSchemeDetails.action", e.target.value)
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>

          {formData.patSchemeDetails.action && (
            <Box sx={{ marginLeft: "1rem", marginTop: "-0.8rem" }}>
              <TextField
                disabled={disabled}
                size="small"
                sx={{ width: "98%", marginTop: "-0.5rem" }}
                placeholder="Type Here"
                value={formData.patSchemeDetails.remark}
                multiline
                onChange={(e) =>
                  handleTextFieldChange2(
                    "patSchemeDetails.remark",
                    e.target.value,
                  )
                }
                error={validationErrors.patSchemeDetails && isSubmitted}
                helperText={
                  validationErrors.patSchemeDetails && isSubmitted ? ( // Only show message if submitted
                    <Typography
                      variant="body2"
                      color="error"
                      style={{
                        lineHeight: "1.2",
                        marginLeft: "-1rem",
                        color: "#d34040",
                        fontSize: "10px",
                        fontStyle: "italic",
                      }}
                    >
                      This field is required
                    </Typography>
                  ) : null
                }
              />
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "patSchemeDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title}>
            3. Provide details of the following disclosures related to water, in
            the following format:
          </p>
          <Box sx={{ marginLeft: "1rem" }}>
            <WaterReusableTable
              disabled={disabled}
              rows={Object.keys(formData.waterDisclosuresRelated)
                .filter((key) =>
                  [
                    "surfaceWater",
                    "groundWater",
                    "thirdPartyWater",
                    "seaWater",
                    "others",
                    "totalVolumnWithdrawal",
                    "totalWaterConsumption",
                    "waterIntensityTurnover",
                    "waterIntensityPuchasingPower",
                    "waterIntensityPhyiscalOutput",
                    "waterIntensityOptional",
                  ].includes(key),
                )
                .map((key) => ({
                  parameter:
                    key === "surfaceWater"
                      ? "(i) Surface water"
                      : key === "groundWater"
                        ? "(ii) Ground water"
                        : key === "thirdPartyWater"
                          ? "(iii) Third party water"
                          : key === "seaWater"
                            ? "(iv) Seawater / desalinated water"
                            : key === "others"
                              ? "(v) Others"
                              : key === "totalVolumnWithdrawal"
                                ? "Total volume of water withdrawal (i + ii + iii + iv + v)"
                                : key === "totalWaterConsumption"
                                  ? "Total volume of water consumption (in kiloliters)"
                                  : key === "waterIntensityTurnover"
                                    ? "Water intensity per rupee of turnover"
                                    : key === "waterIntensityPuchasingPower"
                                      ? "Water intensity per rupee of turnover adjusted for Purchasing Power Parity"
                                      : key === "waterIntensityPhyiscalOutput"
                                        ? "Water intensity in terms of physical output"
                                        : key === "waterIntensityOptional"
                                          ? "Water intensity (optional)"
                                          : "",
                  currentFinancialYear:
                    formData.waterDisclosuresRelated[key]
                      ?.currentFinancialYear ?? "",
                  previousFinancialYear:
                    formData.waterDisclosuresRelated[key]
                      ?.previousFinancialYear ?? "",
                }))}
              columns={[
                {
                  label: "Parameter",
                  field: "parameter",
                  type: "text",
                  editable: false,
                  width: "40%",
                },
                {
                  label: "Current Financial Year (kiloliters)",
                  field: "currentFinancialYear",
                  type: "text",
                  width: "30%",
                },
                {
                  label: "Previous Financial Year (kiloliters)",
                  field: "previousFinancialYear",
                  type: "text",
                  width: "30%",
                },
              ]}
              onChange={(rowIndex, field, value) => {
                setFormData((prev) => {
                  const updatedData = { ...prev };
                  const keys = Object.keys(updatedData.waterDisclosuresRelated);

                  // Update the specific field with the new value
                  updatedData.waterDisclosuresRelated[keys[rowIndex]][field] =
                    value;

                  // Recalculate total water withdrawal
                  updatedData.waterDisclosuresRelated.totalVolumnWithdrawal.currentFinancialYear =
                    [
                      "surfaceWater",
                      "groundWater",
                      "thirdPartyWater",
                      "seaWater",
                      "others",
                    ].reduce(
                      (sum, key) =>
                        sum +
                        (parseFloat(
                          updatedData.waterDisclosuresRelated[key]
                            ?.currentFinancialYear,
                        ) ?? 0),
                      0,
                    );

                  updatedData.waterDisclosuresRelated.totalVolumnWithdrawal.previousFinancialYear =
                    [
                      "surfaceWater",
                      "groundWater",
                      "thirdPartyWater",
                      "seaWater",
                      "others",
                    ].reduce(
                      (sum, key) =>
                        sum +
                        (parseFloat(
                          updatedData.waterDisclosuresRelated[key]
                            ?.previousFinancialYear,
                        ) ?? 0),
                      0,
                    );

                  return updatedData;
                });
              }}
            />
          </Box>

          <p style={{ "text-indent": 0 }} className={style.question_title}>
            If any independent assessment/ evaluation/assurance has been carried
            out by an external agency? (Yes / No)
          </p>
          <Box sx={{ marginLeft: "0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="waterDisclosuresRelated.externalAgency.action"
              value={formData.waterDisclosuresRelated.externalAgency.action}
              onChange={(e) =>
                handleRadioChange(
                  "waterDisclosuresRelated.externalAgency.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>
          {formData.waterDisclosuresRelated.externalAgency.action && (
            <Box sx={{ marginLeft: "1rem" }}>
              <p className={style.question_subtitle}>Name of External Agency</p>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {formData.waterDisclosuresRelated.externalAgency.name?.map(
                  (name, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <TextField
                        disabled={disabled}
                        size="small"
                        sx={{ width: "26rem", marginTop: "-0.8rem" }}
                        placeholder="Enter Name Here"
                        // value={formData.waterDisclosuresRelated.externalAgency.name}
                        value={name}
                        multiline
                        onChange={(e) =>
                          handleTextFieldChange(
                            "waterDisclosuresRelated.externalAgency.name",
                            index,
                            e.target.value,
                          )
                        }
                        error={
                          !!errorMessages.waterDisclosuresRelated
                            .externalAgency?.[index]
                        }
                        helperText={
                          <Typography
                            variant="body2"
                            color="error"
                            style={{
                              lineHeight: "1.2",
                              marginLeft: "-1rem",
                              color: "#d34040",
                              fontSize: "10px",
                              fontStyle: "italic",
                            }}
                          >
                            {errorMessages.waterDisclosuresRelated
                              .externalAgency?.[index] || ""}
                          </Typography>
                        }
                      />
                      {formData.waterDisclosuresRelated.externalAgency.name
                        .length > 1 && (
                        <IconButton
                          disabled={disabled}
                          // onClick={() =>
                          //   handleRemoveField(
                          //     "waterDisclosuresRelated.externalAgency.name",
                          //     index
                          //   )
                          // }
                          onClick={() =>
                            askBeforeDelete(
                              "waterDisclosuresRelated.externalAgency.name",
                              index,
                            )
                          }
                        >
                          <Delete color="error" />
                        </IconButton>
                      )}
                    </Box>
                  ),
                )}

                {!hide && (
                  <Button
                    // disabled={isFilleds || false}

                    startIcon={<Add />}
                    onClick={() =>
                      handleAddField(
                        "waterDisclosuresRelated.externalAgency.name",
                      )
                    }
                    disabled={
                      formData.waterDisclosuresRelated.externalAgency.name[
                        formData.waterDisclosuresRelated.externalAgency.name
                          .length - 1
                      ] === "" || disabled
                    } // Disable if the last field is empty
                    sx={{
                      color: "#1C486B",
                      width: "9rem",
                      borderColor: "#1C486B",
                      marginTop: "-0.8rem",
                      height: "2.5rem",
                    }}
                    variant="outlined"
                  >
                    Add Field
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "waterDisclosuresRelatedDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title}>
            4. Provide the following details related to water discharged:
            <br />
            Water discharge by destination and level of treatment (in
            kilolitres)
          </p>
          <TableContainer
            component={Paper}
            sx={{
              marginLeft: "1rem",
              width: "98%",
              marginTop: "-0.7rem",
              mb: "1rem",
              borderRadius: "10px",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1E4976", color: "#fff" }}>
                  <TableCell
                    sx={{ ...tablestyles.headerCell, width: "30%" }}
                    colSpan={2}
                    rowSpan={2}
                  >
                    Parameter
                  </TableCell>
                  <TableCell sx={{ ...tablestyles.headerCell }} colSpan={2}>
                    Current Financial Year
                  </TableCell>
                  <TableCell sx={{ ...tablestyles.headerCell }} colSpan={2}>
                    Previous Financial Year
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: "#1E4976" }}>
                  <TableCell sx={{ ...tablestyles.headerCell }}>
                    Water Discharge (in KL)
                  </TableCell>
                  <TableCell sx={{ ...tablestyles.headerCell }}>
                    Specify level of treatment
                  </TableCell>
                  <TableCell sx={{ ...tablestyles.headerCell }}>
                    Water Discharged (in KL)
                  </TableCell>
                  <TableCell sx={{ ...tablestyles.headerCell }}>
                    Specify level of treatment
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parameters.map((parameter, index) => (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell sx={{ ...tablestyles.bodyCell }} rowSpan={2}>
                        {parameterDisplayMap[parameter]}
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        Not treated
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        <TextField
                          disabled={disabled}
                          {...tablestyles.textField}
                          placeholder="Enter Number"
                          type="number"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                            step: "any", // Ensures decimal values can be entered if needed
                          }}
                          value={
                            formData.waterDischargeCurrent[parameter]
                              ?.dischargeWithoutTreatment || ""
                          }
                          multiline
                          // onChange={(e) => {
                          //   const value = e.target.value;
                          //   if (/^\d*\.?\d*$/.test(value)) {
                          //     handleTextFieldChange1(
                          //       `waterDischargeCurrent.${parameter}.dischargeWithoutTreatment`,
                          //       e.target.value
                          //     );
                          //   }
                          // }}
                          error={
                            waterDischargeErrors.current[parameter]
                              ?.dischargeWithoutTreatment
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d*$/.test(value)) {
                              handleTextFieldChange1(
                                `waterDischargeCurrent.${parameter}.dischargeWithoutTreatment`,
                                value,
                              );
                              // Clear error when typing
                              setWaterDischargeErrors((prev) => ({
                                ...prev,
                                current: {
                                  ...prev.current,
                                  [parameter]: {
                                    ...prev.current[parameter],
                                    dischargeWithoutTreatment: false,
                                  },
                                },
                              }));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tablestyles.bodyCell,
                          backgroundColor: "#EFEFEF",
                        }}
                      >
                        -
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        <TextField
                          disabled={disabled}
                          {...tablestyles.textField}
                          placeholder="Enter Number"
                          type="number"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                            step: "any", // Ensures decimal values can be entered if needed
                          }}
                          value={
                            formData.waterDischargePrevious[parameter]
                              ?.dischargeWithoutTreatment || ""
                          }
                          multiline
                          // onChange={(e) => {
                          //   const value = e.target.value;
                          //   if (/^\d*\.?\d*$/.test(value)) {
                          //     handleTextFieldChange1(
                          //       `waterDischargePrevious.${parameter}.dischargeWithoutTreatment`,
                          //       e.target.value
                          //     );
                          //   }
                          // }}
                          error={
                            waterDischargeErrors.previous[parameter]
                              ?.dischargeWithoutTreatment
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d*$/.test(value)) {
                              handleTextFieldChange1(
                                `waterDischargePrevious.${parameter}.dischargeWithoutTreatment`,
                                value,
                              );
                              // Clear error when typing
                              setWaterDischargeErrors((prev) => ({
                                ...prev,
                                previous: {
                                  ...prev.previous,
                                  [parameter]: {
                                    ...prev.previous[parameter],
                                    dischargeWithoutTreatment: false,
                                  },
                                },
                              }));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tablestyles.bodyCell,
                          backgroundColor: "#EFEFEF",
                        }}
                      >
                        -
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        With treatment
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        <TextField
                          disabled={disabled}
                          {...tablestyles.textField}
                          placeholder="Enter Number"
                          type="number"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                            step: "any", // Ensures decimal values can be entered if needed
                          }}
                          value={
                            formData.waterDischargeCurrent[parameter]
                              ?.dischargeAfterTreatment || ""
                          }
                          multiline
                          // onChange={(e) => {
                          //   const value = e.target.value;
                          //   if (/^\d*\.?\d*$/.test(value)) {
                          //     handleTextFieldChange1(
                          //       `waterDischargeCurrent.${parameter}.dischargeAfterTreatment`,
                          //       e.target.value
                          //     );
                          //   }
                          // }}

                          error={
                            waterDischargeErrors.current[parameter]
                              ?.dischargeAfterTreatment
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d*$/.test(value)) {
                              handleTextFieldChange1(
                                `waterDischargeCurrent.${parameter}.dischargeAfterTreatment`,
                                value,
                              );
                              // Clear error when typing
                              setWaterDischargeErrors((prev) => ({
                                ...prev,
                                current: {
                                  ...prev.current,
                                  [parameter]: {
                                    ...prev.current[parameter],
                                    dischargeAfterTreatment: false,
                                  },
                                },
                              }));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        <TextField
                          disabled={disabled}
                          {...tablestyles.textField}
                          placeholder="Type Here"
                          value={
                            formData.waterDischargeCurrent[parameter]
                              ?.treatmentLevel || ""
                          }
                          multiline
                          // onChange={(e) =>
                          //   handleTextFieldChange1(
                          //     `waterDischargeCurrent.${parameter}.treatmentLevel`,
                          //     e.target.value
                          //   )
                          // }
                          error={
                            waterDischargeErrors.current[parameter]
                              ?.treatmentLevel
                          }
                          onChange={(e) => {
                            // const value = e.target.value;
                            // if (/^\d*\.?\d*$/.test(value)) {
                            handleTextFieldChange1(
                              `waterDischargeCurrent.${parameter}.treatmentLevel`,
                              e.target.value,
                            );
                            // Clear error when typing
                            setWaterDischargeErrors((prev) => ({
                              ...prev,
                              current: {
                                ...prev.current,
                                [parameter]: {
                                  ...prev.current[parameter],
                                  treatmentLevel: false,
                                },
                              },
                            }));
                            // }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        <TextField
                          disabled={disabled}
                          {...tablestyles.textField}
                          placeholder="Enter Number"
                          type="number"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            min: 0,
                            step: "any", // Ensures decimal values can be entered if needed
                          }}
                          value={
                            formData.waterDischargePrevious[parameter]
                              ?.dischargeAfterTreatment || ""
                          }
                          multiline
                          // onChange={(e) => {
                          //   const value = e.target.value;
                          //   if (/^\d*\.?\d*$/.test(value)) {
                          //     handleTextFieldChange1(
                          //       `waterDischargePrevious.${parameter}.dischargeAfterTreatment`,
                          //       e.target.value
                          //     );
                          //   }
                          // }}
                          error={
                            waterDischargeErrors.previous[parameter]
                              ?.dischargeAfterTreatment
                          }
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d*$/.test(value)) {
                              handleTextFieldChange1(
                                `waterDischargePrevious.${parameter}.dischargeAfterTreatment`,
                                value,
                              );
                              // Clear error when typing
                              setWaterDischargeErrors((prev) => ({
                                ...prev,
                                previous: {
                                  ...prev.previous,
                                  [parameter]: {
                                    ...prev.previous[parameter],
                                    dischargeAfterTreatment: false,
                                  },
                                },
                              }));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...tablestyles.bodyCell }}>
                        <TextField
                          disabled={disabled}
                          {...tablestyles.textField}
                          placeholder="Type Here"
                          value={
                            formData.waterDischargePrevious[parameter]
                              ?.treatmentLevel || ""
                          }
                          multiline
                          // onChange={(e) =>
                          //   handleTextFieldChange1(
                          //     `waterDischargePrevious.${parameter}.treatmentLevel`,
                          //     e.target.value
                          //   )
                          // }

                          error={
                            waterDischargeErrors.previous[parameter]
                              ?.treatmentLevel
                          }
                          onChange={(e) => {
                            // const value = e.target.value;
                            // if (/^\d*\.?\d*$/.test(value)) {
                            handleTextFieldChange1(
                              `waterDischargePrevious.${parameter}.treatmentLevel`,
                              e.target.value,
                            );
                            // Clear error when typing
                            setWaterDischargeErrors((prev) => ({
                              ...prev,
                              previous: {
                                ...prev.previous,
                                [parameter]: {
                                  ...prev.previous[parameter],
                                  treatmentLevel: false,
                                },
                              },
                            }));
                            // }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      ...tablestyles.bodyCell,
                      fontWeight: 600,
                      py: "0.7rem",
                    }}
                  >
                    Total water discharged (in kilolitres)
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{
                      ...tablestyles.bodyCell,
                      backgroundColor: "#EFEFEF",
                      color: "#a3a3a3",
                    }}
                  >
                    {" "}
                    {calculateTotalDischarge("waterDischargeCurrent") ??
                      "Total"}
                  </TableCell>

                  <TableCell
                    colSpan={2}
                    sx={{
                      ...tablestyles.bodyCell,
                      backgroundColor: "#EFEFEF",
                      color: "#a3a3a3",
                    }}
                  >
                    {calculateTotalDischarge("waterDischargePrevious") ??
                      "Total"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {getWaterDischargeErrorMessages().length > 0 && (
            <Box sx={{ mt: 1, ml: 3, color: "error.main" }}>
              {getWaterDischargeErrorMessages().map((message, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  color="error"
                  style={{
                    lineHeight: "1.2",
                    margin: "0 0 0 -0.8rem",
                    color: "#d34040",
                    fontSize: "10px",
                    fontStyle: "italic",
                  }}
                >
                  • {message}
                </Typography>
              ))}
            </Box>
          )}

          <p style={{ "text-indent": 0 }} className={style.question_title}>
            If any independent assessment/ evaluation/assurance has been carried
            out by an external agency? (Yes / No)
          </p>
          <Box sx={{ marginLeft: "0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="waterDischargeExternalAgency.action"
              value={formData.waterDischargeExternalAgency.action}
              onChange={(e) =>
                handleRadioChange(
                  "waterDischargeExternalAgency.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>

          {formData.waterDischargeExternalAgency.action && (
            <Box>
              <p
                style={{ marginLeft: "1rem" }}
                className={style.question_subtitle}
              >
                Name of External Agency
              </p>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {formData.waterDischargeExternalAgency.name?.map(
                  (name, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <TextField
                        disabled={disabled}
                        size="small"
                        sx={{
                          width: "26rem",
                          marginLeft: "1rem",
                          marginTop: "-0.7rem",
                        }}
                        placeholder="Enter Name Here"
                        // value={formData.waterDischargeExternalAgency.name}
                        value={name}
                        multiline
                        onChange={(e) =>
                          handleTextFieldChange(
                            "waterDischargeExternalAgency.name",
                            index,
                            e.target.value,
                          )
                        }
                        error={
                          !!errorMessages.waterDischargeExternalAgency?.[index]
                        }
                        helperText={
                          <Typography
                            variant="body2"
                            color="error"
                            style={{
                              lineHeight: "1.2",
                              marginLeft: "-1rem",
                              color: "#d34040",
                              fontSize: "10px",
                              fontStyle: "italic",
                            }}
                          >
                            {errorMessages.waterDischargeExternalAgency?.[
                              index
                            ] || ""}
                          </Typography>
                        }
                      />
                      {formData.waterDischargeExternalAgency.name.length >
                        1 && (
                        <IconButton
                          disabled={disabled}
                          // onClick={() =>
                          //   handleRemoveField(
                          //     "waterDischargeExternalAgency.name",
                          //     index
                          //   )
                          // }
                          onClick={() =>
                            askBeforeDelete(
                              "waterDischargeExternalAgency.name",
                              index,
                            )
                          }
                        >
                          <Delete color="error" />
                        </IconButton>
                      )}
                    </Box>
                  ),
                )}

                {!hide && (
                  <Button
                    // disabled={isFilleds || false}
                    startIcon={<Add />}
                    onClick={() =>
                      handleAddField("waterDischargeExternalAgency.name")
                    }
                    disabled={
                      formData.waterDischargeExternalAgency.name[
                        formData.waterDischargeExternalAgency.name.length - 1
                      ] === "" || disabled
                    } // Disable if the last field is empty
                    sx={{
                      color: "#1C486B",
                      width: "9rem",
                      borderColor: "#1C486B",
                      marginTop: "-0.8rem",
                      height: "2.5rem",
                    }}
                    variant="outlined"
                  >
                    Add Field
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "waterDischargeDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {/* {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>} */}
        <Box sx={{ mb: 3 }}>
          <p
            className={style.question_title}
            style={{ textIndent: "-1.1rem", paddingLeft: "1.1rem" }}
          >
            5. Has the entity implemented a mechanism for Zero Liquid Discharge?
            If yes, provide details of its coverage and implementation.
          </p>
          <Box sx={{ marginLeft: "0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="zeroLiquidDischarge.action"
              value={formData.zeroLiquidDischarge.action}
              onChange={(e) =>
                handleRadioChange("zeroLiquidDischarge.action", e.target.value)
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>
          {formData.zeroLiquidDischarge.action && (
            <TextField
              disabled={disabled}
              minRows={3}
              style={{
                width: "100%",
                padding: "8px",
                background: "transparent",
                // border: "1px solid #74757E",
                marginLeft: "0.5rem",
                marginTop: "-0.3rem",
                borderRadius: "4px",
              }}
              multiline
              placeholder="Type Here"
              value={formData.zeroLiquidDischarge.remark}
              onChange={(e) =>
                handleTextFieldChange2(
                  "zeroLiquidDischarge.remark",
                  e.target.value,
                )
              }
              error={validationErrors.zeroLiquidDischarge && isSubmitted}
              helperText={
                validationErrors.zeroLiquidDischarge && isSubmitted ? ( // Only show message if submitted
                  <Typography
                    variant="body2"
                    color="error"
                    style={{
                      lineHeight: "1.2",
                      marginLeft: "-1rem",
                      color: "#d34040",
                      fontSize: "10px",
                      fontStyle: "italic",
                    }}
                  >
                    This field is required
                  </Typography>
                ) : null
              }
            />
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "zeroLiquidDischargeDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4.9rem" }}>
            <p className={style.question_title}>
              6. Please provide details of air emissions (other than GHG
              emissions) by the entity, in the following format:
            </p>
            {/* Dropdown */}
            <FormControl
              size="small"
              variant="outlined"
              error={isSubmitted && unitErrors.selectedUnit1}
              sx={{ minWidth: 170, marginBottom: "1.8rem" }}
            >
              <Select
                value={selectedUnit1}
                disabled={disabled}
                onChange={(e) => {
                  setSelectedUnit1(e.target.value);
                  if (e.target.value) {
                    setUnitErrors((prev) => ({
                      ...prev,
                      selectedUnit1: false,
                    }));
                  }
                }}
                displayEmpty
                sx={{
                  height: "34px",
                  fontSize: "14px",
                  padding: "2px 10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      isSubmitted && unitErrors.selectedUnit1
                        ? "#d32f2f"
                        : "#ccc",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Unit
                </MenuItem>
                <MenuItem value="kilogram (kg)">kilogram (kg)</MenuItem>
                <MenuItem value="Metric tons (mt)">Metric tonnes (mt)</MenuItem>
                <MenuItem value="Giga tons (gt)">Giga tonnes (gt)</MenuItem>
                <MenuItem value="ppm">ppm</MenuItem>
                <MenuItem value="mg/Nm3">mg/Nm3</MenuItem>
              </Select>
              {isSubmitted && unitErrors.selectedUnit1 && (
                <FormHelperText
                  sx={{
                    color: "#d32f2f",
                    marginTop: "4px",
                    fontSize: "12px",
                    marginLeft: "0rem",
                  }}
                >
                  Unit is required
                </FormHelperText>
              )}
            </FormControl>
          </div>
          <Box sx={{ marginLeft: "1rem" }}>
            <ReusableTable
              disabled={disabled}
              data={airEmissions}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="airEmissons"
              labels={wasteRecycledLabels}
              reusableErrors={reusableErrors}
            />
          </Box>

          <Box sx={{ marginLeft: "1rem", mt: 2 }}>
            <p
              className={style.question_title}
              style={{ marginBottom: "0.5rem" }}
            >
              Other air emission parameters (if any)
            </p>
            <TableContainer
              component={Paper}
              sx={{ borderRadius: "10px", width: "97%", mb: 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1E4976" }}>
                    <TableCell
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        fontFamily: "inter",
                      }}
                    >
                      Parameter
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        fontFamily: "inter",
                      }}
                    >
                      Current Financial Year
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        fontFamily: "inter",
                      }}
                    >
                      Previous Financial Year
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        fontFamily: "inter",
                        width: "60px",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(formData.airEmissionsOthers || []).map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          disabled={disabled}
                          size="small"
                          fullWidth
                          multiline
                          placeholder="Enter parameter name"
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            className: "textField",
                          }}
                          value={row.parameter}
                          onChange={(e) =>
                            handleOtherAirEmissionChange(
                              index,
                              "parameter",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          disabled={disabled}
                          size="small"
                          fullWidth
                          multiline
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            className: "textField",
                          }}
                          placeholder="Enter value"
                          value={row.currentFinancialYear}
                          onKeyPress={(e) => {
                            if (!/[0-9.]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            handleOtherAirEmissionChange(
                              index,
                              "currentFinancialYear",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          disabled={disabled}
                          size="small"
                          fullWidth
                          multiline
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            className: "textField",
                          }}
                          placeholder="Enter value"
                          value={row.previousFinancialYear}
                          onKeyPress={(e) => {
                            if (!/[0-9.]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) =>
                            handleOtherAirEmissionChange(
                              index,
                              "previousFinancialYear",
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        {formData.airEmissionsOthers &&
                          formData.airEmissionsOthers.length > 1 && (
                            <IconButton
                              disabled={disabled}
                              onClick={() =>
                                handleRemoveOtherAirEmissionRow(index)
                              }
                            >
                              <Delete color="error" />
                            </IconButton>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ marginLeft: "1rem", mt: 1 }}>
            <Button
              startIcon={<Add />}
              variant="outlined"
              disabled={
                disabled ||
                !(
                  formData.airEmissionsOthers &&
                  formData.airEmissionsOthers.length > 0 &&
                  (() => {
                    const last =
                      formData.airEmissionsOthers[
                        formData.airEmissionsOthers.length - 1
                      ] || {};
                    return (
                      (last.parameter || "").toString().trim() !== "" &&
                      (last.currentFinancialYear || "").toString().trim() !==
                        "" &&
                      (last.previousFinancialYear || "").toString().trim() !==
                        ""
                    );
                  })()
                )
              }
              sx={{
                color: "#1C486B",
                borderColor: "#1C486B",
                height: "2.2rem",
                mt: 0.5,
              }}
              onClick={handleAddOtherAirEmissionRow}
            >
              Add Other Parameter
            </Button>
          </Box>

          <p
            style={{ "text-indent": 0, marginTop: "1rem" }}
            className={style.question_title}
          >
            If any independent assessment/ evaluation/assurance has been carried
            out by an external agency? (Yes / No)
          </p>
          <Box sx={{ marginLeft: "0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="airEmissionAssessment.action"
              value={formData.airEmissionAssessment.action}
              onChange={(e) =>
                handleRadioChange(
                  "airEmissionAssessment.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>
          {formData.airEmissionAssessment.action && (
            <Box>
              <p
                style={{ marginLeft: "1rem" }}
                className={style.question_subtitle}
              >
                Name of External Agency
              </p>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {formData.airEmissionAssessment.name?.map((name, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <TextField
                      disabled={disabled}
                      size="small"
                      sx={{
                        width: "26rem",
                        marginLeft: "1rem",
                        marginTop: "-0.8rem",
                      }}
                      placeholder="Enter Name Here"
                      multiline
                      // value={formData.airEmissionAssessment.name}
                      value={name}
                      onChange={(e) =>
                        handleTextFieldChange(
                          "airEmissionAssessment.name",
                          index,
                          e.target.value,
                        )
                      }
                      error={!!errorMessages.airEmissionAssessment?.[index]}
                      helperText={
                        <Typography
                          variant="body2"
                          color="error"
                          style={{
                            lineHeight: "1.2",
                            marginLeft: "-1rem",
                            color: "#d34040",
                            fontSize: "10px",
                            fontStyle: "italic",
                          }}
                        >
                          {errorMessages.airEmissionAssessment?.[index] || ""}
                        </Typography>
                      }
                    />
                    {formData.airEmissionAssessment.name.length > 1 && (
                      <IconButton
                        disabled={disabled}
                        // onClick={() =>
                        //   handleRemoveField("airEmissionAssessment.name", index)
                        // }
                        onClick={() =>
                          askBeforeDelete("airEmissionAssessment.name", index)
                        }
                      >
                        <Delete color="error" />
                      </IconButton>
                    )}
                  </Box>
                ))}

                {!hide && (
                  <Button
                    // disabled={isFilleds || false}

                    startIcon={<Add />}
                    onClick={() => handleAddField("airEmissionAssessment.name")}
                    disabled={
                      formData.airEmissionAssessment.name[
                        formData.airEmissionAssessment.name.length - 1
                      ] === "" || disabled
                    } // Disable if the last field is empty
                    sx={{
                      color: "#1C486B",
                      width: "9rem",
                      borderColor: "#1C486B",
                      marginTop: "-0.8rem",
                      height: "2.5rem",
                    }}
                    variant="outlined"
                  >
                    Add Field
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "airEmissionDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title}>
            7. Provide details of greenhouse gas emissions (Scope 1 and Scope 2
            emissions) & its intensity, in the following format:
          </p>
          <Box sx={{ marginLeft: "1rem" }}>
            <ReusableTable
              disabled={disabled}
              data={greenGasEmissions}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="greenHouseGasEmissions"
              labels={greenGasLabels}
              reusableErrors={reusableErrors}
            />
          </Box>

          <p style={{ "text-indent": 0 }} className={style.question_title}>
            If any independent assessment/ evaluation/assurance has been carried
            out by an external agency? (Yes / No)
          </p>
          <Box sx={{ marginLeft: "0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="greenHouseGasEmissions.externalAgency.action"
              value={formData.greenHouseGasEmissions.externalAgency.action}
              onChange={(e) =>
                handleRadioChange(
                  "greenHouseGasEmissions.externalAgency.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>

          {formData.greenHouseGasEmissions.externalAgency.action && (
            <Box>
              <p
                style={{ marginLeft: "1rem" }}
                className={style.question_subtitle}
              >
                Name of External Agency
              </p>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {formData.greenHouseGasEmissions.externalAgency.name?.map(
                  (name, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <TextField
                        disabled={disabled}
                        size="small"
                        sx={{
                          width: "26rem",
                          marginLeft: "1rem",
                          marginTop: "-0.8rem",
                        }}
                        placeholder="Enter Name Here"
                        // value={formData.greenHouseGasEmissions.externalAgency.name}
                        value={name}
                        multiline
                        onChange={(e) =>
                          handleTextFieldChange(
                            "greenHouseGasEmissions.externalAgency.name",
                            index,
                            e.target.value,
                          )
                        }
                        error={
                          !!errorMessages.greenHouseGasEmissions
                            .externalAgency?.[index]
                        }
                        helperText={
                          <Typography
                            variant="body2"
                            color="error"
                            style={{
                              lineHeight: "1.2",
                              marginLeft: "-1rem",
                              color: "#d34040",
                              fontSize: "10px",
                              fontStyle: "italic",
                            }}
                          >
                            {errorMessages.greenHouseGasEmissions
                              .externalAgency?.[index] || ""}
                          </Typography>
                        }
                      />
                      {formData.greenHouseGasEmissions.externalAgency.name
                        .length > 1 && (
                        <IconButton
                          // onClick={() =>
                          //   handleRemoveField(
                          //     "greenHouseGasEmissions.externalAgency.name",
                          //     index
                          //   )
                          // }
                          onClick={() =>
                            askBeforeDelete(
                              "greenHouseGasEmissions.externalAgency.name",
                              index,
                            )
                          }
                        >
                          <Delete color="error" />
                        </IconButton>
                      )}
                    </Box>
                  ),
                )}

                {!hide && (
                  <Button
                    // disabled={isFilleds || false}
                    startIcon={<Add />}
                    onClick={() =>
                      handleAddField(
                        "greenHouseGasEmissions.externalAgency.name",
                      )
                    }
                    disabled={
                      formData.greenHouseGasEmissions.externalAgency.name[
                        formData.greenHouseGasEmissions.externalAgency.name
                          .length - 1
                      ] === ""
                    } // Disable if the last field is empty
                    sx={{
                      color: "#1C486B",
                      width: "9rem",
                      borderColor: "#1C486B",
                      marginTop: "-1rem",
                      height: "2.5rem",
                    }}
                    variant="outlined"
                  >
                    Add Field
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "greenHouseGasEmissionsDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title2}>
            8. Does the entity have any project related to reducing Green House
            Gas emission? If Yes, then provide details.
          </p>
          <Box sx={{ marginLeft: "0.4rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="greenHouseGasBooleanEmissions.action"
              value={formData.greenHouseGasBooleanEmissions.action}
              onChange={(e) =>
                handleRadioChange(
                  "greenHouseGasBooleanEmissions.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>
          {formData.greenHouseGasBooleanEmissions.action && (
            <TextField
              minRows={3}
              disabled={disabled}
              multiline
              style={{
                width: "100%",
                padding: "8px",
                background: "transparent",
                borderRadius: "4px",
                marginTop: "-0.5rem",
                marginLeft: "0.6rem",
              }}
              placeholder="Enter Details"
              value={formData.greenHouseGasBooleanEmissions.remark}
              onChange={(e) =>
                handleTextFieldChange2(
                  "greenHouseGasBooleanEmissions.remark",
                  e.target.value,
                )
              }
              error={
                validationErrors.greenHouseGasBooleanEmissions && isSubmitted
              }
              helperText={
                validationErrors.greenHouseGasBooleanEmissions &&
                isSubmitted ? ( // Only show message if submitted
                  <Typography
                    variant="body2"
                    color="error"
                    style={{
                      lineHeight: "1.2",
                      marginLeft: "-1rem",
                      color: "#d34040",
                      fontSize: "10px",
                      fontStyle: "italic",
                    }}
                  >
                    This field is required
                  </Typography>
                ) : null
              }
            />
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "greenHouseGasBooleanEmissionsDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title}>
            9. Provide details related to waste management by the entity
          </p>
          <Box sx={{ marginLeft: "1rem" }}>
            <p className={style.question_title}>
              a. Waste generated (in metric tonnes)
            </p>
            <ReusableTable
              disabled={disabled}
              data={wasteGenerated}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="wasteGenerated"
              labels={wasteRecycledLabels}
              reusableErrors={reusableErrors}
            />
            {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
            <p className={style.question_title} style={{ marginTop: "1.5rem" }}>
              b. Waste intensity
            </p>
            <ReusableTable
              disabled={disabled}
              data={wasteIntensity}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="wasteIntensity"
              labels={wasteIntensityColumnLabels}
              reusableErrors={reusableErrors}
              wrapValueCells
              digitOnlyKeys={WASTE_INTENSITY_NUMERIC_ROW_KEYS}
            />
            {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
            <p className={style.question_title} style={{ marginTop: "1.5rem" }}>
              c. For each category of waste generated, total waste recovered
              through recycling, re-using or other recovery operations (in
              metric tonnes)
            </p>
            <ReusableTable
              disabled={disabled}
              data={wasteRecycledCategory}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="wasteGeneratedCategory"
              labels={wasteRecycledLabels}
              reusableErrors={reusableErrors}
            />
            <p className={style.question_title} style={{ marginTop: "1.5rem" }}>
              d. For each category of waste generated, total waste disposed by
              nature of disposal method (in metric tonnes)
            </p>
            <ReusableTable
              disabled={disabled}
              data={wasteCategory}
              formData={formData}
              handleInputChange={handleInputChange}
              fieldPrefix="wasteDesposed"
              labels={wasteRecycledLabels}
              reusableErrors={reusableErrors}
            />
          </Box>

          <p style={{ "text-indent": 0 }} className={style.question_title}>
            Indicate if any independent assessment/ evaluation/assurance has
            been carried out by an external agency? If yes, name of the external
            agency.
          </p>
          <Box sx={{ marginLeft: "0.3rem" }}>
            <ReusableRadioGroup
              disabled={disabled}
              name="independentAssessment.action"
              value={formData.independentAssessment.action}
              onChange={(e) =>
                handleRadioChange(
                  "independentAssessment.action",
                  e.target.value,
                )
              }
              labels={{ yes: "Yes", no: "No" }}
            />
          </Box>

          {formData.independentAssessment.action && (
            <Box>
              <p
                style={{ marginLeft: "1rem" }}
                className={style.question_subtitle}
              >
                Name of External Agency
              </p>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {formData.independentAssessment.name?.map((name, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <TextField
                      disabled={disabled}
                      size="small"
                      sx={{
                        width: "26rem",
                        marginLeft: "1rem",
                        marginTop: "-0.8rem",
                      }}
                      placeholder="Enter Name Here"
                      multiline
                      // value={formData.independentAssessment.name}
                      value={name}
                      onChange={(e) =>
                        handleTextFieldChange(
                          "independentAssessment.name",
                          index,
                          e.target.value,
                        )
                      }
                      error={!!errorMessages.independentAssessment?.[index]}
                      helperText={
                        <Typography
                          variant="body2"
                          color="error"
                          style={{
                            lineHeight: "1.2",
                            marginLeft: "-1rem",
                            color: "#d34040",
                            fontSize: "10px",
                            fontStyle: "italic",
                          }}
                        >
                          {errorMessages.independentAssessment?.[index] || ""}
                        </Typography>
                      }
                    />
                    {formData.independentAssessment.name.length > 1 && (
                      <IconButton
                        disabled={disabled}
                        // onClick={() =>
                        //   handleRemoveField("independentAssessment.name", index)
                        // }
                        onClick={() =>
                          askBeforeDelete("independentAssessment.name", index)
                        }
                      >
                        <Delete color="error" />
                      </IconButton>
                    )}
                  </Box>
                ))}

                {!hide && (
                  <Button
                    // disabled={isFilleds || false}
                    startIcon={<Add />}
                    onClick={() => handleAddField("independentAssessment.name")}
                    disabled={
                      formData.independentAssessment.name[
                        formData.independentAssessment.name.length - 1
                      ] === "" || disabled
                    } // Disable if the last field is empty
                    sx={{
                      color: "#1C486B",
                      width: "9rem",
                      borderColor: "#1C486B",
                      marginTop: "-0.8rem",
                      height: "2.5rem",
                    }}
                    variant="outlined"
                  >
                    Add Field
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "wasteManagementDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title} style={{ textIndent: "-1.6rem" }}>
            10. Briefly describe the waste management practices adopted in your
            establishments. Describe the strategy adopted by your company to
            reduce usage of hazardous and toxic chemicals in your products and
            processes and the practices adopted to manage such wastes.
          </p>
          <p style={{ marginLeft: "1rem" }} className={style.question_subtitle}>
            Provide steps taken to address the same.
          </p>
          <TextField
            minRows={3}
            disabled={disabled}
            style={{
              width: "100%",
              padding: "8px",
              background: "transparent",
              borderRadius: "4px",
              marginLeft: "0.5rem",
              marginTop: "-1rem",
            }}
            multiline
            placeholder="Type Here"
            value={formData.wasterManagmentPractices}
            onChange={(e) =>
              handleTextFieldChange2("wasterManagmentPractices", e.target.value)
            }
            error={!!validationErrors.wasterManagmentPractices}
            helperText={
              <Typography
                variant="body2"
                color="error"
                style={{
                  lineHeight: "1.2",
                  margin: "2px 0",
                  color: "#d34040",
                  fontSize: "10px",
                  fontStyle: "italic",
                  marginLeft: "-10px",
                }}
              >
                {validationErrors.wasterManagmentPractices}
              </Typography>
            }
          />
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "wasterManagmentPracticesDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title} style={{ textIndent: "-1.4rem" }}>
            11. If the entity has operations/offices in/around ecologically
            sensitive sensitive areas (such as national parks, wildlife
            sanctuaries, biosphere reserves, wetlands, biodiversity hotspots,
            forests, coastal regulation zones etc.) where environmental
            approvals / clearances are required, please specify details in the
            following format:
          </p>
          <Box sx={{ marginLeft: "1rem" }}>
            <DynamicTable
              disabled={disabled}
              data={formData.environmentalApproval}
              columns={operationsColumns}
              setRows={setRows}
              fieldDisabled={undefined}
              errors={errors?.environmentalApproval}
              onRowChange={(rowIndex, key, value) =>
                handleTableChange("environmentalApproval", rowIndex, key, value)
              }
              onAddRow={(defaultRow) =>
                addRowToTable("environmentalApproval", defaultRow)
              }
              onDeleteRow={(rowIndex) =>
                handleDeleteRow("environmentalApproval", rowIndex, [
                  "location",
                  "operation",
                  "approval",
                  "reason",
                ])
              }
              cancelRemoveRow={cancelRemoveRow}
              openDialog={openDialog}
              confirmRemoveRow={confirmRemoveRow}
              defaultRow={{
                serialNumber: formData.environmentalApproval.length + 1,
                location: "",
                operation: "",
                approval: "",
                reason: "",
              }}
              hide={hide}
            />
          </Box>
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "environmentalApprovalDoc",
            ml: 2,
            removeUpload,
          }}
        />
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title} style={{ textIndent: "-1.6rem" }}>
            12. Details of environmental impact assessments of projects
            undertaken by the entity based on applicable laws, in the current
            financial year:
          </p>
          <RadioGroup
            row
            disabled={disabled}
            value={formData.eiaEnabled ?? "Yes"}
            onChange={(e) =>
              handleApplicableChange("eiaEnabled", e.target.value)
            }
            sx={{
              marginLeft: "1.25rem",
              marginTop: "-0.5rem",
              marginBottom: "0.5rem",
            }}
          >
            <FormControlLabel
              value={"Yes"}
              control={<Radio disabled={disabled} />}
              label="Applicable"
            />
            <FormControlLabel
              value={"No"}
              control={<Radio disabled={disabled} />}
              label="Not Applicable"
            />
          </RadioGroup>
          {isApplicableYes(formData.eiaEnabled) && (
          <Box sx={{ marginLeft: "1rem" }}>
            <DynamicTable
              disabled={disabled}
              data={formData.projectUndertakenByentity}
              columns={environmentAssesmentColumns}
              setRows={setRows}
              errors={errors?.projectUndertakenByentity}
              onRowChange={(rowIndex, key, value) =>
                handleTableChange(
                  "projectUndertakenByentity",
                  rowIndex,
                  key,
                  value,
                )
              }
              onAddRow={(defaultRow) =>
                addRowToTable("projectUndertakenByentity", defaultRow)
              }
              onDeleteRow={(rowIndex) =>
                handleDeleteRow("projectUndertakenByentity", rowIndex, [
                  "nameOfProject",
                  "projectBrief",
                  "EIANotification",
                  "dateOfNotification",
                  "publicDomain",
                  "extenalAgecy",
                  "link",
                ])
              }
              defaultRow={{
                serialNumber: formData.projectUndertakenByentity.length + 1,
                ...emptyProjectUndertakenRow,
              }}
              hide={hide}
            />
          </Box>
          )}
        </Box>
        <BRSRFile
          {...{
            modalupload,
            setModalUpload,
            formData,
            setFormData,
            errors,
            setErrors,
            Uploadkey: "projectUndertakenByentityDoc",
            ml: 2,
            removeUpload,
          }}
        />
        {downloadMode && <Box sx={{ pageBreakBefore: "always" }}></Box>}
        <Box sx={{ mb: 3 }}>
          <p className={style.question_title} style={{ textIndent: "-1.6rem" }}>
            13. Is the entity compliant with the applicable environmental law/
            regulations/ guidelines in India; such as the Water (Prevention and
            Control of Pollution) Act, Air (Prevention and Control of Pollution)
            Act, Environment protection act and rules thereunder. If not,
            provide details of all such non-compliances, in the following
            format:
          </p>
          <FormControl component="fieldset">
            <RadioGroup
              disabled={disabled}
              name="waterIndependantAssessment"
              value={
                formData.entityComplain === "true" ||
                formData.entityComplain === true
                  ? "yes"
                  : "no"
              }
              style={{
                display: "flex",
                flexDirection: "row",
                marginBottom: "10px",
                marginTop: "-10px",
                margin: "-10px 0px 10px 15px",
              }}
              onChange={(e) => {
                const value = e.target.value === "yes";
                setFormData((prevFormData) => {
                  const updatedData = {
                    ...prevFormData,
                    entityComplain: value,
                  };

                  // Clear related fields when "no" is selected
                  if (!value) {
                    updatedData.entityComplainwithEnvironmentLaw = [
                      {
                        serialNumber: 1,
                        specifyLaw: "",
                        details: "",
                        penalties: "",
                        correctiveAction: "",
                      },
                    ];
                    // Clear errors for this field
                    setErrors((prevErrors) => {
                      const updatedErrors = { ...prevErrors };
                      delete updatedErrors.entityComplainwithEnvironmentLaw;
                      return updatedErrors;
                    });
                  }

                  return updatedData;
                });
              }}
            >
              <FormControlLabel
                value="yes"
                control={<Radio disabled={disabled} />}
                label="Yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio disabled={disabled} />}
                label="No"
              />
            </RadioGroup>
          </FormControl>

          {(formData.entityComplain === "true" ||
            formData.entityComplain === true) && (
            <Box sx={{ marginLeft: "1rem" }}>
              <DynamicTable
                disabled={disabled}
                hide={hide}
                data={formData.entityComplainwithEnvironmentLaw}
                columns={environmentColumns}
                setRows={setRows}
                errors={errors?.entityComplainwithEnvironmentLaw}
                onRowChange={(rowIndex, key, value) =>
                  handleTableChange(
                    "entityComplainwithEnvironmentLaw",
                    rowIndex,
                    key,
                    value,
                  )
                }
                onAddRow={(defaultRow) =>
                  addRowToTable("entityComplainwithEnvironmentLaw", defaultRow)
                }
                onDeleteRow={(rowIndex) =>
                  handleDeleteRow(
                    "entityComplainwithEnvironmentLaw",
                    rowIndex,
                    ["specifyLaw", "details", "penalties", "correctiveAction"],
                  )
                }
                defaultRow={{
                  serialNumber:
                    formData.entityComplainwithEnvironmentLaw.length + 1,
                  specifyLaw: "",
                  details: "",
                  penalties: "",
                  correctiveAction: "",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <BRSRFile
        {...{
          modalupload,
          setModalUpload,
          formData,
          setFormData,
          errors,
          setErrors,
          Uploadkey: "entityawDoc",
          ml: 2,
          removeUpload,
        }}
      />
      {!hide && (
        <>
          {brsrReview ? (
            <Box sx={{ ml: "1rem", width: "98.5%" }}>
              <ReviewBox
                url={`/brsr/review/${id}`}
                handleSubmit={handleSubmit}
              />
            </Box>
          ) : (
            <Box textAlign="right" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                sx={{ bgcolor: "#1C486B", textTransform: "none", mt: 2, mb: 5 }}
                onClick={handleSubmit}
              >
                Save & Continue <EastIcon sx={{ ml: 2 }} />
              </Button>
            </Box>
          )}
        </>
      )}
      <Snackbar
        open={snackOpen.open}
        message={snackOpen.message}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          severity={snackOpen.severity || "info"} // Ensure a default severity if undefined
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message || "No message provided"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EssentialIndicator;
